import { useNotifications } from '../../hooks/useNotifications';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { api, Job, ContractorJob, ConnectStatusResponse, PayoutHistoryItem, RetryPayoutsResult, ContractorTierStats, AchievementItem, MessageItem, AvailabilitySlot, ContractorAvailabilityResponse, DisclosureItem, ServiceCatalogItemDto, JobLineItemDto, EstimateDto, InvoiceDto, PaymentScheduleDto, ContractorSkillDto, InventoryItemDto, InventoryUsageDto, ShreddingBinDto, BinScanEventDto } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useFeature } from '../../context/FeatureFlagContext';
import { useIdleDisconnect } from '../../hooks/useIdleDisconnect';
import { PageShell, Card, Btn, Badge, Alert, Spinner, StatCard, Input, C } from '../../components/UI';
import { useBranding } from '../../context/BrandingContext';
import { PhotoModal } from '../../components/PhotoModal';
import { ContractorEstimatesView } from './ContractorEstimatesView';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type View = 'feed' | 'myjobs' | 'calendar' | 'earnings' | 'tier' | 'onboarding' | 'availability' | 'skills' | 'estimates';

// contractorPayoutAmount is the job-split share only — the configurable job fee has its own,
// separate contractor split, so what a contractor actually receives (one combined Stripe
// Transfer) is always the sum of both. Never display contractorPayoutAmount alone.
const payoutFor = (job: ContractorJob) => job.contractorPayoutAmount + (job.serviceFeeContractorPayout ?? 0);

export default function ContractorPortal() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const loadingJobsEnabled = useFeature('loadingJobs');
    const bgCheckRequired = useFeature('backgroundCheckRequired');
    const certTrackingEnabled = useFeature('contractorCertTracking');
    const estimateBuilderEnabled = useFeature('estimateBuilder');
    const [view, setView] = useState<View>('feed');
    const [nearbyJobs, setNearbyJobs] = useState<ContractorJob[]>([]);
    const [myJobs, setMyJobs] = useState<ContractorJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newJobAlert, setNewJobAlert] = useState(false);
    const [nearbyCount, setNearbyCount] = useState(0);
    const hubRef = useRef<signalR.HubConnection | null>(null);
    const pollRef = useRef<NodeJS.Timeout | null>(null);
    const coordsRef = useRef({ lat: 34.7304, lng: -86.5861 });
    const [photoModal, setPhotoModal] = useState<{ url: string; units: number; address: string } | null>(null);
    const [bgCheckStatus, setBgCheckStatus] = useState<{ status: string; invitationUrl?: string; canAcceptJobs: boolean } | null>(null);
    const [docStatus, setDocStatus] = useState<any>(null);
    const [loadingJobs, setLoadingJobs] = useState<any[]>([]); // open jobs
    const [myLoadingJobs, setMyLoadingJobs] = useState<any[]>([]); // accepted by me
    const [pendingDisclosures, setPendingDisclosures] = useState<DisclosureItem[] | null>(null);
    const [disclosureScrolled, setDisclosureScrolled] = useState(false);
    const [disclosureAgreed, setDisclosureAgreed] = useState(false);
    const [disclosureLoading, setDisclosureLoading] = useState(false);
    const [disclosureOptOut, setDisclosureOptOut] = useState(false);
    const disclosureBodyRef = useRef<HTMLDivElement>(null);
    // Separate from disclosureBodyRef on purpose — see the effect below for why.
    const disclosureContentRef = useRef<HTMLDivElement>(null);

    useNotifications(user?.userId ?? null, user?.role ?? null);
    useEffect(() => { if (!user || user.role !== 'Contractor') navigate('/'); }, [user, navigate]);

    useEffect(() => {
        if (!user || user.role !== 'Contractor') return;
        api.getPendingDisclosures().then(pending => {
            if (pending.length > 0) setPendingDisclosures(pending);
        }).catch(() => {});
    }, [user]);

    // Auto-enable scroll gate when full document fits without scrolling. A single one-shot
    // requestAnimationFrame check here used to measure scrollHeight/clientHeight right at mount
    // — before the page's web font (Google Fonts "Inter") necessarily finishes loading. The
    // fallback font can render the text a line taller than "Inter" does once it loads, so the
    // one-shot check could fail at that exact instant and never re-run — leaving the gate stuck
    // locked forever even after the real, settled layout no longer needs scrolling at all.
    //
    // First attempt at fixing this observed disclosureBodyRef (the scroll container) itself with
    // a ResizeObserver — which does NOT work: that element has overflow-y: auto specifically so
    // its OWN box size never changes when its content reflows (that's what overflow:auto means).
    // ResizeObserver only fires when the *observed element's own* size changes, not when a
    // scrollable descendant's content height changes inside it — confirmed live (reporter's own
    // build still got stuck after that fix deployed). Observing disclosureContentRef instead — the
    // inner wrapper around the actual text, which is NOT overflow-constrained and genuinely
    // resizes when the font swaps — while still comparing against the outer container's
    // clientHeight for the actual fits/doesn't-fit decision.
    useEffect(() => {
        if (!pendingDisclosures) return;
        setDisclosureScrolled(false);
        setDisclosureAgreed(false);
        const outer = disclosureBodyRef.current;
        const inner = disclosureContentRef.current;
        if (!outer || !inner) return;
        const checkFits = () => {
            if (outer.scrollHeight <= outer.clientHeight + 5) setDisclosureScrolled(true);
        };
        checkFits();
        const observer = new ResizeObserver(checkFits);
        observer.observe(inner);
        return () => observer.disconnect();
    }, [pendingDisclosures]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                pos => {
                    coordsRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    fetchNearby();
                },
                () => console.log('Geolocation unavailable, using default coords')
            );
        }
    }, []);

    const fetchNearby = useCallback(async () => {
        try {
            const nearby = await api.getNearbyJobs(coordsRef.current.lat, coordsRef.current.lng, 50);
            setNearbyJobs(nearby);
            setNearbyCount(nearby.length);
        } catch (e) { console.error('fetchNearby error:', e); }
    }, []);

    const fetchLoadingJobs = useCallback(async () => {
        try {
            const [open, mine] = await Promise.all([
                api.getOpenLoadingJobs(coordsRef.current.lat, coordsRef.current.lng, 500),
                api.getMyLoadingJobs(),
            ]);
            setLoadingJobs(open);
            setMyLoadingJobs(mine);
        } catch (e) { console.error('fetchLoadingJobs error:', e); }
    }, []);

    // Guards against overlapping calls — fetchAll now fires from several independent triggers
    // (mount, hub reconnect, tab focus), and an older in-flight call resolving after a newer one
    // can otherwise stomp fresh state (e.g. a status change just made by the user) with stale data.
    const fetchInFlightRef = useRef(false);
    const fetchAgainRef = useRef(false);
    // Only the very first fetch should show the full-page spinner. fetchAll also fires silently
    // in the background (tab focus/visibility regained, SignalR reconnect) — previously it set
    // `loading` every time, and the `if (loading) return <Spinner />` early-return below then
    // unmounted whatever the contractor was in the middle of. That included the onboarding form:
    // opening the native file picker to attach a license/insurance/vehicle photo blurs the page,
    // and the browser's focus/visibilitychange events on dialog-close fired this refetch, wiping
    // every typed field (and the photo just picked) right as the dialog closed.
    const hasLoadedRef = useRef(false);
    const fetchAll = useCallback(async () => {
        if (fetchInFlightRef.current) { fetchAgainRef.current = true; return; }
        fetchInFlightRef.current = true;
        if (!hasLoadedRef.current) setLoading(true);
        try {
            const [nearby, mine] = await Promise.all([
                api.getNearbyJobs(coordsRef.current.lat, coordsRef.current.lng, 50),
                api.getContractorJobs(),
            ]);
            setNearbyJobs(nearby);
            setNearbyCount(nearby.length);
            setMyJobs(mine);
        } catch (e: any) { setError(e.message); }
        finally {
            setLoading(false);
            hasLoadedRef.current = true;
            fetchInFlightRef.current = false;
            if (fetchAgainRef.current) { fetchAgainRef.current = false; fetchAll(); }
        }
    }, []);

    const connectHub = useCallback(() => {
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        hub.on('NewJobAvailable', () => {
            fetchNearby();
            fetchLoadingJobs();
            setNewJobAlert(true);
            setTimeout(() => setNewJobAlert(false), 10000);
        });

        hub.on('JobStatusUpdated', (push: { jobStatus: string; jobId: string }) => {
            if (push.jobStatus === 'Completed') {
                setMyJobs(prev => prev.map(j =>
                    j.id === push.jobId ? { ...j, status: 'Completed' } : j
                ));
            }
        });

        hub.on('PriceAdjustmentUpdated', (push: { jobId: string; status: string; adjustedPrice: number; approvedAt: string }) => {
            setMyJobs(prev => prev.map(j =>
                j.id === push.jobId
                    ? { ...j, priceAdjustmentStatus: push.status, adjustedPrice: push.adjustedPrice }
                    : j
            ));
        });
        hub.on('JobRated', (push: { jobId: string; stars: number; comment?: string; createdAt: string }) => {
            setMyJobs(prev => prev.map(j =>
                j.id === push.jobId
                    ? { ...j, rating: { stars: push.stars, comment: push.comment, createdAt: push.createdAt } }
                    : j
            ));
        });

        hub.on('JobRescheduled', (data: { jobId: string; scheduledDate: string | null; scheduledTimeSlot: string | null }) => {
            setMyJobs(prev => prev.map(j =>
                j.id === data.jobId
                    ? { ...j, scheduledDate: data.scheduledDate ?? undefined, scheduledTimeSlot: data.scheduledTimeSlot ?? undefined }
                    : j
            ));
        });

        hub.on('LoadingJobCompleted', () => {
            fetchLoadingJobs();
        });

        hub.on('DumpsterOrderUpdated', () => {
            fetchLoadingJobs();
        });

        hub.onreconnected(() => {
            hub.invoke('JoinContractorPool').catch(() => { });
            setMyJobs(current => {
                current
                    .filter(j => !['Completed', 'Cancelled'].includes(j.status))
                    .forEach(j => hub.invoke('JoinJobAsContractor', j.id).catch(() => { }));
                return current;
            });
            // Some assignment paths (e.g. admin dispatching an estimate visit) never fire a
            // SignalR event at all, only a push — and anything that WAS missed while the socket
            // was down doesn't get replayed. Refresh directly on reconnect to catch both.
            fetchAll();
        });

        hub.start()
            .then(() => hub.invoke('JoinContractorPool').catch(() => { }))
            .catch(console.error);

        hubRef.current = hub;
    }, [fetchNearby, fetchLoadingJobs, fetchAll]);

    useEffect(() => {
        fetchAll();
        fetchLoadingJobs();
        connectHub();
        pollRef.current = setInterval(() => { fetchNearby(); fetchLoadingJobs(); }, 30000);
        return () => {
            hubRef.current?.stop();
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [fetchAll, fetchLoadingJobs, connectHub, fetchNearby]);

    // Idle-gated, but NEVER while actively working a job — a contractor driving hands-off to a
    // job (or mid-job) must not lose the channel that delivers a mid-drive reschedule/dispatch
    // update just because their hands are off the phone. This is deliberately separate from (and
    // must never touch) the per-job 5-second location-ping intervals in ActiveJobCard/
    // WebLoadingJobCard below, which are already correctly gated on job.status themselves.
    const hasActiveDrivingOrWorkingJob =
        myJobs.some(j => ['EnRoute', 'Arrived', 'InProgress'].includes(j.status)) ||
        myLoadingJobs.some((j: any) => ['EnRoute', 'InProgress'].includes(j.status));
    useIdleDisconnect({
        suppressIdle: hasActiveDrivingOrWorkingJob,
        onIdle: () => {
            hubRef.current?.stop().catch(() => {});
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        },
        onActive: () => {
            if (hubRef.current?.state === signalR.HubConnectionState.Disconnected) connectHub();
            if (!pollRef.current) {
                fetchNearby();
                fetchLoadingJobs();
                pollRef.current = setInterval(() => { fetchNearby(); fetchLoadingJobs(); }, 30000);
            }
        },
    });

    // Refresh when the tab regains focus — some assignment paths never fire a SignalR event,
    // and a long-backgrounded tab's socket may have dropped without reconnecting cleanly, so
    // returning to the tab is what actually catches a job assigned while it was away.
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') fetchAll(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [fetchAll]);

    useEffect(() => {
        if (bgCheckRequired) api.getMyBackgroundCheckStatus()
            .then(s => setBgCheckStatus({ status: s.status, invitationUrl: s.invitationUrl, canAcceptJobs: s.canAcceptJobs }))
            .catch(() => {});
        api.getMyDocumentStatus()
            .then(setDocStatus)
            .catch(() => {});
        // loading jobs are now fetched by fetchLoadingJobs() in the main useEffect
    }, []);

    const acceptJob = async (jobId: string) => {
        setError('');
        try {
            const job = await api.acceptJob(jobId);
            hubRef.current?.invoke('JoinJobAsContractor', jobId).catch(() => { });
            setNearbyJobs(prev => prev.filter(j => j.id !== jobId));
            setNearbyCount(prev => Math.max(0, prev - 1));
            setMyJobs(prev => [job, ...prev]);
            setView('myjobs');
        } catch (e: any) { setError(e.message); }
    };

    const updateStatus = async (jobId: string, status: string, actualUnits?: number) => {
        setError('');
        try {
            const updated = await api.updateStatus(jobId, status);
            if (actualUnits && status === 'Completed') {
                await api.updateActualUnits(jobId, actualUnits);
            }
            setMyJobs(prev => prev.map(j => j.id === jobId ? updated : j));
        } catch (e: any) { setError(e.message); }
    };

    // Both /api/price-adjustment/.../request (matching-units path) and /api/.../complete-catalog
    // already set Job.Status = Completed server-side as part of that one call — there's no
    // separate status transition left to make. Re-POSTing through updateStatus afterward hit
    // JobService.UpdateStatusAsync's "already Completed" guard and surfaced as a scary error even
    // though the job had, in fact, completed successfully. This just patches local state to match
    // what the server already did, with no further network call.
    const markCompletedLocally = (jobId: string, actualUnits?: number) => {
        setMyJobs(prev => prev.map(j => j.id === jobId
            ? { ...j, status: 'Completed', actualUnits: actualUnits ?? j.actualUnits }
            : j));
    };

    const requestEarlyStart = async (jobId: string) => {
        try {
            const updated = await api.requestEarlyStart(jobId);
            setMyJobs(prev => prev.map(j => j.id === jobId ? updated : j));
        } catch (e: any) { setError(e.message); }
    };

    const sendLocation = async (jobId: string) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async pos => { await api.sendLocation(jobId, pos.coords.latitude, pos.coords.longitude); },
                () => {
                    const lat = coordsRef.current.lat + (Math.random() - 0.5) * 0.02;
                    const lng = coordsRef.current.lng + (Math.random() - 0.5) * 0.02;
                    api.sendLocation(jobId, lat, lng);
                }
            );
        } else {
            const lat = coordsRef.current.lat + (Math.random() - 0.5) * 0.02;
            const lng = coordsRef.current.lng + (Math.random() - 0.5) * 0.02;
            await api.sendLocation(jobId, lat, lng);
        }
    };

    const activeJobs = myJobs.filter(j => !['Completed', 'Cancelled'].includes(j.status));
    const activeLoadingJobs = myLoadingJobs.filter((j: any) => !['Completed', 'Cancelled'].includes(j.status));
    const completedJobs = myJobs.filter(j => j.status === 'Completed');
    const completedLoadingJobs = myLoadingJobs.filter((j: any) => j.status === 'Completed');
    const totalCompletedCount = completedJobs.length + completedLoadingJobs.length;
    const totalEarnings = completedJobs.reduce((s, j) => s + payoutFor(j), 0)
        + completedLoadingJobs.reduce((s: number, j: any) => s + (j.finalCost ?? 0) * 0.8, 0);

    const nav = (
        <>
            {(['feed', 'myjobs', 'calendar', 'earnings', 'tier', 'availability', ...(certTrackingEnabled ? ['skills'] as View[] : []), ...(estimateBuilderEnabled ? ['estimates'] as View[] : []), 'onboarding'] as View[]).map(v => (
                <button key={v} onClick={() => setView(v)} style={{ background: 'transparent', border: 'none', borderBottom: `2px solid ${view === v ? 'var(--color-accent, #4ADE80)' : 'transparent'}`, color: view === v ? 'var(--color-accent, #4ADE80)' : 'rgba(255,255,255,0.7)', padding: '6px 14px', borderRadius: 0, cursor: 'pointer', fontWeight: view === v ? 700 : 400, fontSize: 13 }}>
                    {v === 'feed'
                        ? <>Available{(nearbyCount + (loadingJobsEnabled ? loadingJobs.length : 0)) > 0 && <span style={{ background: newJobAlert ? '#EF9F27' : 'rgba(255,255,255,0.3)', borderRadius: 10, padding: '1px 7px', marginLeft: 6, fontSize: 11, fontWeight: 700 }}>{nearbyCount + (loadingJobsEnabled ? loadingJobs.length : 0)}</span>}</>
                        : v === 'myjobs' ? `My Jobs (${activeJobs.length + activeLoadingJobs.length})`
                        : v === 'calendar' ? '📅 Calendar'
                        : v === 'earnings' ? '💰 Earnings'
                        : v === 'tier' ? '🏆 Tier & Badges'
                        : v === 'availability' ? '🗓 Availability'
                        : v === 'skills' ? '🔧 Skills'
                        : v === 'estimates' ? '📋 Estimate Requests'
                        : '📋 Documents'}
                </button>
            ))}
            <button onClick={() => { logout(); navigate('/'); }} style={{ background: 'transparent', border: '1px solid var(--color-accent, #4ADE80)', color: 'var(--color-accent, #4ADE80)', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Sign out</button>
        </>
    );

    if (loading) return <PageShell title="Contractor" nav={nav}><Spinner /></PageShell>;

    const hasArbitration = pendingDisclosures?.some(d => d.type === 'TermsOfService') ?? false;

    const handleDisclosureScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setDisclosureScrolled(true);
    };

    const handleAgree = async () => {
        if (!pendingDisclosures || !disclosureAgreed) return;
        setDisclosureLoading(true);
        try {
            for (const d of pendingDisclosures) {
                await api.acceptDisclosure(d.id, d.type === 'TermsOfService' ? disclosureOptOut : false);
            }
            setPendingDisclosures(null);
        } catch (e: any) { alert(e.message); }
        finally { setDisclosureLoading(false); }
    };

    return (
        <>
        <PageShell title={`${user?.name?.split(' ')[0]} · Contractor`} nav={nav}>
            {newJobAlert && (
                <div style={{ background: '#EF9F27', color: '#fff', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    🔔 New job available nearby!
                </div>
            )}

            {/* Onboarding banner */}
            {docStatus && docStatus.onboardingStatus !== 'Approved' && (
                <div style={{ background: '#EEF2FF', border: '1px solid #6366F1', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#3730A3', marginBottom: 4 }}>
                            📋 {docStatus.onboardingStatus === 'UnderReview' ? 'Documents under review' : 'Complete your onboarding'}
                        </div>
                        <div style={{ fontSize: 13, color: '#4338CA' }}>
                            {docStatus.onboardingStatus === 'UnderReview'
                                ? 'Your documents have been submitted and are being reviewed by an admin.'
                                : "Upload your driver's license, proof of insurance, and vehicle info to start accepting jobs."}
                        </div>
                    </div>
                    {docStatus.onboardingStatus !== 'UnderReview' && (
                        <Btn size="sm" onClick={() => setView('onboarding')}>Complete onboarding →</Btn>
                    )}
                </div>
            )}

            {/* Background check banner */}
            {bgCheckRequired && bgCheckStatus && !bgCheckStatus.canAcceptJobs && (
                <div style={{ borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid', ...getBgCheckBannerStyle(bgCheckStatus.status) }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                                {getBgCheckBannerIcon(bgCheckStatus.status)} {getBgCheckBannerTitle(bgCheckStatus.status)}
                            </div>
                            <div style={{ fontSize: 13, opacity: 0.85 }}>{getBgCheckBannerMessage(bgCheckStatus.status)}</div>
                        </div>
                        {bgCheckStatus.status === 'Pending' && (
                            <Btn size="sm" onClick={async () => {
                                try {
                                    const res = await api.initiateMyBackgroundCheck();
                                    if (res.invitationUrl) window.open(res.invitationUrl, '_blank');
                                    setBgCheckStatus({ status: res.status, invitationUrl: res.invitationUrl, canAcceptJobs: res.canAcceptJobs });
                                } catch (e: any) { alert(e.message); }
                            }}>
                                Start background check →
                            </Btn>
                        )}
                        {bgCheckStatus.invitationUrl && bgCheckStatus.status === 'Invited' && (
                            <Btn size="sm" onClick={() => window.open(bgCheckStatus.invitationUrl!, '_blank')}>
                                Complete my check →
                            </Btn>
                        )}
                    </div>
                </div>
            )}
            {bgCheckStatus?.canAcceptJobs && (
                <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                    <span style={{ fontSize: 18 }}>🛡</span>
                    <span style={{ color: C.green, fontWeight: 600 }}>Background check cleared — you're verified!</span>
                </div>
            )}

            <Alert message={error} />
            {view !== 'earnings' && view !== 'tier' && view !== 'onboarding' && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
                    <StatCard label="Total earnings" value={`$${totalEarnings.toFixed(2)}`} color={C.greenMid} />
                    <StatCard label="Jobs completed" value={totalCompletedCount} />
                    <StatCard label="Active jobs" value={activeJobs.length} />
                </div>
            )}

            {view === 'feed' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <h3 style={{ margin: 0, fontSize: 15, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Jobs near you</h3>
                        <Btn size="sm" variant="outline" onClick={fetchNearby}>↻ Refresh</Btn>
                    </div>
                    {/* Loading Jobs */}
                    {loadingJobsEnabled && loadingJobs.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                            <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#3730A3', textTransform: 'uppercase', letterSpacing: 0.5 }}>💪 Loading Jobs</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {loadingJobs.map((job: any) => (
                                    <Card key={job.id} style={{ borderLeft: '4px solid #6366F1' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>🚛 Dumpster Loading Job</div>
                                                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>📍 {job.deliveryAddress}</div>
                                                {job.loadingScheduledAt && (
                                                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>
                                                        📅 {new Date(job.loadingScheduledAt).toLocaleDateString()} {new Date(job.loadingScheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 12, color: C.textMuted }}>
                                                    $10/hr · {job.runnersRequested} runner{job.runnersRequested > 1 ? 's' : ''} needed
                                                </div>
                                            </div>
                                            <Btn size="sm" onClick={async () => {
                                                try {
                                                    await api.acceptLoadingJob(job.id);
                                                    setLoadingJobs(prev => prev.filter((j: any) => j.id !== job.id));
                                                    const updated = await api.getMyLoadingJobs();
                                                    setMyLoadingJobs(updated);
                                                } catch (e: any) { setError(e.message); }
                                            }}>
                                                Accept
                                            </Btn>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}

                    {nearbyJobs.length === 0 ? (
                        <Card style={{ textAlign: 'center', padding: 48 }}>
                            <div style={{ fontSize: 40 }}>🔍</div>
                            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No jobs nearby right now</div>
                            <div style={{ color: C.textSub, marginTop: 6 }}>New jobs will appear here instantly when customers book.</div>
                        </Card>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {nearbyJobs.map(job => (
                                <Card key={job.id}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Badge label={job.serviceType} />
                                                <span style={{ background: '#E1F5EE', color: '#085041', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>New</span>
                                            </div>
                                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{job.pickupAddress}</div>
                                            <div style={{ fontSize: 13, color: C.textSub }}>{job.estimatedUnits} units · {job.scheduledTimeSlot || 'ASAP'}</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: C.greenMid, marginBottom: 8 }}>${payoutFor(job).toFixed(2)}</div>
                                            <Btn onClick={() => acceptJob(job.id)} size="sm">Accept</Btn>
                                        </div>
                                    </div>
                                    {job.photoUrl && (
                                        <div style={{ marginTop: 12, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, cursor: 'pointer' }}
                                            onClick={() => setPhotoModal({ url: job.photoUrl!, units: job.estimatedUnits, address: job.pickupAddress })}
                                        >
                                            <img src={job.photoUrl} alt="Pickup pile" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            <div style={{ padding: '6px 10px', background: C.bg, fontSize: 11, color: C.textSub, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span>📷 Customer photo · {job.estimatedUnits} units estimated</span>
                                                <span style={{ color: C.greenMid, fontWeight: 500 }}>Tap to expand</span>
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {view === 'myjobs' && (
                <div>
                    {activeJobs.length > 0 && (
                        <>
                            <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Active</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                                {activeJobs.map(job => (
                                    <ActiveJobCard key={job.id} job={job} onUpdateStatus={updateStatus} onJobCompletedLocally={markCompletedLocally} onSendLocation={sendLocation} onGoToEstimates={() => setView('estimates')} onRequestEarlyStart={requestEarlyStart} />
                                ))}
                            </div>
                        </>
                    )}

                    {/* Active Loading Jobs */}
                    {activeLoadingJobs.length > 0 && (
                        <>
                            <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#3730A3', textTransform: 'uppercase', letterSpacing: 0.5 }}>💪 Loading Jobs</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                                {activeLoadingJobs.map((job: any) => (
                                    <WebLoadingJobCard key={job.id} job={job} onRefresh={() => {
                                        api.getMyLoadingJobs().then(setMyLoadingJobs).catch(() => {});
                                    }} />
                                ))}
                            </div>
                        </>
                    )}
                    {(completedJobs.length > 0 || completedLoadingJobs.length > 0) && (
                        <>
                            <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Completed ({totalCompletedCount})</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {completedLoadingJobs.map((job: any) => (
                                    <Card key={`loading-${job.id}`} style={{ borderLeft: `4px solid ${C.greenMid}` }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>🚛 Loading Job</div>
                                                <div style={{ fontSize: 13, color: C.textSub }}>{job.deliveryAddress}</div>
                                                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{job.hoursWorked?.toFixed(2)}h worked</div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontWeight: 700, color: C.greenMid, fontSize: 16 }}>${(job.finalCost * 0.8).toFixed(2)}</div>
                                                <div style={{ fontSize: 11, color: C.textMuted }}>your payout</div>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                                {completedJobs.map(job => <CompletedContractorJobCard key={job.id} job={job} />)}
                            </div>
                        </>
                    )}
                    {myJobs.length === 0 && (
                        <Card style={{ textAlign: 'center', padding: 48 }}>
                            <div style={{ fontSize: 40 }}>📋</div>
                            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No jobs yet</div>
                            <div style={{ color: C.textSub, marginTop: 6 }}>Accept a job from the Available tab to get started.</div>
                        </Card>
                    )}
                </div>
            )}
            {view === 'calendar' && <ContractorCalendarView myJobs={myJobs} onRefresh={fetchAll} />}
            {view === 'earnings' && <EarningsView completedLoadingJobs={completedLoadingJobs} />}
            {view === 'tier' && <TierView />}
            {view === 'availability' && <AvailabilityView />}
            {view === 'skills' && <SkillsView />}
            {view === 'estimates' && <ContractorEstimatesView />}
            {view === 'onboarding' && <OnboardingView docStatus={docStatus} onStatusChange={setDocStatus} />}
            {photoModal && (
                <PhotoModal photoUrl={photoModal.url} estimatedUnits={photoModal.units} address={photoModal.address} onClose={() => setPhotoModal(null)} />
            )}
        </PageShell>

        {/* Disclosure modal — single combined document, one agree at the bottom */}
        {pendingDisclosures && pendingDisclosures.length > 0 && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ width: '100%', maxWidth: 720, maxHeight: '90vh', background: C.card, borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 48px rgba(0,0,0,0.3)' }}>
                    {/* Header */}
                    <div style={{ background: '#1e293b', padding: '16px 24px', flexShrink: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{contractorLabel} Agreements & Disclosures</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{pendingDisclosures.length} required document{pendingDisclosures.length > 1 ? 's' : ''} — please read in full before agreeing</div>
                        {!disclosureScrolled && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#fbbf24' }}>↓ Scroll to the bottom to enable "I Agree"</div>
                        )}
                    </div>

                    {/* Single scrollable document containing all disclosures */}
                    <div
                        ref={disclosureBodyRef}
                        onScroll={handleDisclosureScroll}
                        style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

                        <div ref={disclosureContentRef}>
                        {pendingDisclosures.map((d, i) => (
                            <div key={d.id}>
                                {i > 0 && <hr style={{ border: 'none', borderTop: `2px solid ${C.border}`, margin: '32px 0' }} />}
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                                        Document {i + 1} of {pendingDisclosures.length}
                                    </div>
                                    <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 4 }}>{d.title}</div>
                                    <div style={{ fontSize: 11, color: C.textMuted }}>Version {d.version} · {new Date(d.updatedAt).toLocaleDateString()}</div>
                                </div>
                                <div style={{ fontSize: 14, lineHeight: 1.8, color: C.text, whiteSpace: 'pre-wrap' }}>{d.body}</div>
                            </div>
                        ))}

                        {/* Arbitration opt-out (only if ToS is in the set) */}
                        {hasArbitration && (
                            <div style={{ marginTop: 32, padding: '16px 20px', background: '#fff8e7', border: '1px solid #f0c060', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <input type="checkbox" id="opt-out" checked={disclosureOptOut} onChange={e => setDisclosureOptOut(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
                                <label htmlFor="opt-out" style={{ fontSize: 13, color: C.text, cursor: 'pointer', lineHeight: 1.6 }}>
                                    <strong>Arbitration opt-out:</strong> I wish to opt out of the binding arbitration clause in the Terms of Service (must be exercised within 30 days of signing).
                                </label>
                            </div>
                        )}

                        {/* Single agree section at the very bottom */}
                        <div style={{ marginTop: 32, padding: '20px 24px', background: C.bg, border: `2px solid ${disclosureScrolled ? C.greenMid : C.border}`, borderRadius: 10, transition: 'border-color 0.2s' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                                <input
                                    type="checkbox"
                                    id="agree-all"
                                    checked={disclosureAgreed}
                                    disabled={!disclosureScrolled}
                                    onChange={e => setDisclosureAgreed(e.target.checked)}
                                    style={{ marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: disclosureScrolled ? 'pointer' : 'not-allowed' }}
                                />
                                <label htmlFor="agree-all" style={{ fontSize: 14, color: disclosureScrolled ? C.text : C.textMuted, cursor: disclosureScrolled ? 'pointer' : 'not-allowed', lineHeight: 1.6, fontWeight: 500 }}>
                                    I have read and agree to all of the above agreements and disclosures.
                                </label>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
                                {!disclosureScrolled && (
                                    <span style={{ fontSize: 12, color: C.textMuted }}>Scroll to the bottom to unlock</span>
                                )}
                                <Btn
                                    onClick={handleAgree}
                                    disabled={!disclosureScrolled || !disclosureAgreed || disclosureLoading}>
                                    {disclosureLoading ? 'Saving…' : '✓ I Agree to All'}
                                </Btn>
                            </div>
                        </div>
                        </div>

                        <div style={{ height: 16 }} />
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

// ── Completed job row — TREK-79: pick up the auto-generated invoice once it's ready ─────

function CompletedContractorJobCard({ job }: { job: ContractorJob }) {
    const [invoice, setInvoice] = useState<InvoiceDto | null>(null);

    useEffect(() => {
        api.getInvoice(job.id).then(setInvoice).catch(() => {});
    }, [job.id]);

    return (
        <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{job.pickupAddress}</div>
                    <div style={{ fontSize: 13, color: C.textSub }}>{job.serviceType} · {job.estimatedUnits} units</div>
                    {job.rating && <div style={{ fontSize: 13, color: C.yellow, marginTop: 4 }}>{'★'.repeat(job.rating.stars)} {job.rating.comment && `· "${job.rating.comment}"`}</div>}
                    {(job.priceAdjustmentStatus === 'Approved' || job.priceAdjustmentStatus === 'AdminApproved') && (
                        <div style={{ fontSize: 12, marginTop: 6 }}>
                            <span style={{ background: '#E1F5EE', color: '#085041', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>✓ Price adjustment applied</span>
                        </div>
                    )}
                    {invoice && (
                        <div style={{ marginTop: 8 }}>
                            <Btn size="sm" variant="outline" onClick={() => api.downloadInvoicePdf(job.id, invoice.number)}>📄 Invoice</Btn>
                        </div>
                    )}
                </div>
                <div style={{ textAlign: 'right', marginLeft: 12 }}>
                    <div style={{ fontWeight: 700, color: C.greenMid, fontSize: 16 }}>${payoutFor(job).toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>your pay</div>
                </div>
            </div>
        </Card>
    );
}

// ── Document Shredding — contractor confirms declared categories before completion ──────────
// Completion is also gated server-side (JobService.UpdateStatusAsync), so this is a convenience,
// not the only enforcement — a contractor who skips this just gets a clear error on "Complete."

function ShreddingCategoriesConfirm({ job }: { job: ContractorJob }) {
    const [categories, setCategories] = useState<string[]>([]);
    const [confirmed, setConfirmed] = useState(job.shredding?.categoriesConfirmed ?? false);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.getShreddingJobCategories(job.id).then(setCategories).catch(() => setCategories([])).finally(() => setLoading(false));
    }, [job.id]);

    const confirm = async () => {
        setConfirming(true); setError('');
        try {
            await api.confirmShreddingCategories(job.id);
            setConfirmed(true);
        } catch (e: any) { setError(e.message || 'Failed to confirm.'); }
        finally { setConfirming(false); }
    };

    return (
        <div style={{ marginTop: 8, padding: '8px 10px', background: confirmed ? C.greenBg : '#FFF8E6', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: confirmed ? C.green : '#92700A', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Document categories
            </div>
            {!loading && categories.length > 0 && (
                <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>{categories.join(', ')}</div>
            )}
            {confirmed ? (
                <div style={{ fontSize: 12, color: C.greenMid, fontWeight: 600, marginTop: 2 }}>✓ Confirmed</div>
            ) : (
                <Btn variant="outline" onClick={confirm} disabled={confirming} style={{ marginTop: 6, fontSize: 12, padding: '6px 12px' }}>
                    {confirming ? 'Confirming…' : 'Confirm before completing'}
                </Btn>
            )}
            {error && <div style={{ fontSize: 11, color: '#C0392B', marginTop: 4 }}>{error}</div>}
        </div>
    );
}

// ── Document Shredding — web bin-scan fallback (no camera, manual bin label entry) ──────────
// Mobile scans the bin's QR directly; web has no camera-based scanning, so the contractor types
// the bin's label instead. Same checkpoints, same audit trail either way.
// Each (request type, service location) combination gets its own subset — a bin-delivery job
// never destroys anything, a destruction-service visit never drops off or retrieves a bin (it's
// already there), on-site destruction never leaves the property (no transit/facility legs), and a
// retrieval never destroys anything either. Mirrors ShreddingService.AllowedCheckpointsByType on
// the backend, which is what actually enforces this.
const CHECKPOINTS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
    BinDelivery: [
        { value: 'DroppedOffAtCustomer', label: 'Dropped off empty bin at customer' },
    ],
    'DestructionService:OnSite': [
        { value: 'ArrivedOnSite', label: 'Arrived on-site to service existing bin' },
        { value: 'ApprovedForDestruction', label: 'Approved contents for destruction' },
        { value: 'DestructionConfirmed', label: 'Destruction confirmed on-site' },
        { value: 'ReturnedToCustomer', label: 'Returned emptied bin to customer' },
    ],
    'DestructionService:OffSite': [
        { value: 'ArrivedOnSite', label: 'Arrived on-site to service existing bin' },
        { value: 'ApprovedForDestruction', label: 'Approved contents for destruction' },
        { value: 'LoadedOnTruck', label: 'Loaded onto truck — en route to destruction facility' },
        { value: 'ArrivedAtFacility', label: 'Arrived at destruction facility' },
        { value: 'DestructionConfirmed', label: 'Destruction confirmed at facility' },
        { value: 'ReturnedToCustomer', label: 'Returned emptied bin to customer' },
    ],
    BinRetrieval: [
        { value: 'ArrivedOnSite', label: 'Arrived on-site to pick up bin' },
        { value: 'ReturnedToWarehouse', label: 'Returned bin to warehouse' },
    ],
};

function checkpointsFor(requestType?: string, serviceLocation?: string) {
    if (requestType === 'DestructionService') {
        return CHECKPOINTS_BY_TYPE[`DestructionService:${serviceLocation === 'OnSite' ? 'OnSite' : 'OffSite'}`];
    }
    return CHECKPOINTS_BY_TYPE[requestType ?? ''] ?? CHECKPOINTS_BY_TYPE['DestructionService:OffSite'];
}

function ShreddingBinScanPanel({ job }: { job: ContractorJob }) {
    const SHREDDING_CHECKPOINTS = checkpointsFor(job.shredding?.requestType, job.shredding?.serviceLocation);
    const [label, setLabel] = useState('');
    const [checkpoint, setCheckpoint] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [bins, setBins] = useState<ShreddingBinDto[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [scans, setScans] = useState<BinScanEventDto[]>([]);
    const labelInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        api.getContractorShreddingBins().then(setBins).catch(() => { /* typeahead is a convenience, not required to record a scan */ });
    }, []);

    const matches = label.trim()
        ? bins.filter(b => b.label.toLowerCase().includes(label.trim().toLowerCase()))
        : bins;

    const selectedBin = bins.find(b => b.label.toLowerCase() === label.trim().toLowerCase()) || null;

    // Reload this bin's scan history (to know which checkpoints are already done for this job)
    // whenever the resolved bin changes — e.g. after picking a suggestion or typing an exact label.
    useEffect(() => {
        if (!selectedBin) { setScans([]); return; }
        api.getContractorShreddingBinScans(selectedBin.id).then(setScans).catch(() => setScans([]));
    }, [selectedBin?.id]);

    const doneForThisJob = new Set(scans.filter(s => s.jobId === job.id).map(s => s.scanType));
    const nextStep = SHREDDING_CHECKPOINTS.find(c => !doneForThisJob.has(c.value));
    const activeCheckpoint = checkpoint ?? nextStep?.value ?? SHREDDING_CHECKPOINTS[SHREDDING_CHECKPOINTS.length - 1].value;

    const pick = (b: ShreddingBinDto) => {
        setLabel(b.label);
        setShowSuggestions(false);
        setSuccess('');
        setCheckpoint(null); // re-derive the next step for the newly picked bin
    };

    // Explicit, unambiguous way to pick a different bin after a wrong one — typing over the
    // text works too, but a contractor who picked the wrong bin from the dropdown shouldn't have
    // to figure that out; this always resets to a clean, focused, empty field.
    const clear = () => {
        setLabel('');
        setError('');
        setSuccess('');
        setCheckpoint(null);
        setShowSuggestions(true);
        labelInputRef.current?.focus();
    };

    const submit = async () => {
        if (!label.trim()) { setError('Enter the bin label first.'); return; }
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.scanShreddingBinByLabel(label.trim(), activeCheckpoint, job.id);
            setSuccess(`Recorded: ${SHREDDING_CHECKPOINTS.find(c => c.value === activeCheckpoint)?.label}`);
            setCheckpoint(null); // advance to whatever's next once history reloads
            if (selectedBin) api.getContractorShreddingBinScans(selectedBin.id).then(setScans).catch(() => {});
        } catch (e: any) { setError(e.message || 'Failed to record scan.'); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ marginTop: 8, padding: '8px 10px', background: C.bg, borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Bin chain of custody
            </div>
            {job.shredding?.sizeName && (
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                    Customer ordered: <span style={{ color: C.green }}>{job.shredding.sizeName}</span>
                </div>
            )}
            <div style={{ position: 'relative', marginBottom: 10 }}>
                <input
                    ref={labelInputRef}
                    value={label}
                    onChange={e => { setLabel(e.target.value); setError(''); setSuccess(''); setShowSuggestions(true); setCheckpoint(null); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder="Bin label, e.g. Bin #014"
                    style={{ width: '100%', padding: '7px 32px 7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box' }}
                />
                {label && (
                    <button
                        type="button"
                        onMouseDown={clear}
                        aria-label="Clear bin"
                        style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 22, height: 22, borderRadius: '50%', border: 'none', background: C.border,
                            color: C.textSub, fontSize: 13, lineHeight: '22px', textAlign: 'center', padding: 0,
                            cursor: 'pointer',
                        }}
                    >
                        ✕
                    </button>
                )}
                {showSuggestions && matches.length > 0 && (
                    <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 6,
                        maxHeight: 160, overflowY: 'auto', marginTop: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}>
                        {matches.map(b => (
                            <div
                                key={b.id}
                                onMouseDown={() => pick(b)}
                                style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                            >
                                <span>
                                    <span style={{ fontWeight: 600 }}>{b.label}</span>
                                    {b.sizeName && <span style={{ color: C.textSub }}> — {b.sizeName}</span>}
                                </span>
                                <span style={{ fontSize: 11, color: C.textMuted, flexShrink: 0 }}>{b.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {selectedBin && (
                <div style={{ fontSize: 12, color: C.textSub, marginTop: -4, marginBottom: 10 }}>
                    {selectedBin.sizeName ? (
                        <>Selected: <strong style={{ color: C.text }}>{selectedBin.sizeName}</strong></>
                    ) : (
                        <span style={{ color: '#C0392B' }}>This bin has no size on file.</span>
                    )}
                </div>
            )}

            {/* Tracker — each checkpoint in order; done ones are checked off (for this job),
                the active one (tap any to select it, defaults to the first undone step) is
                highlighted, and the rest are pending. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 10 }}>
                {SHREDDING_CHECKPOINTS.map((c, i) => {
                    const done = doneForThisJob.has(c.value);
                    const active = !done && c.value === activeCheckpoint;
                    const isLast = i === SHREDDING_CHECKPOINTS.length - 1;
                    return (
                        <div key={c.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                                <div style={{
                                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 11, fontWeight: 700,
                                    background: done ? C.greenMid : active ? '#fff' : C.card,
                                    border: `2px solid ${done ? C.greenMid : active ? C.greenMid : C.border}`,
                                    color: done ? '#fff' : active ? C.greenMid : C.textMuted,
                                }}>
                                    {done ? '✓' : i + 1}
                                </div>
                                {!isLast && <div style={{ width: 2, flex: 1, minHeight: 14, background: done ? C.greenMid : C.border }} />}
                            </div>
                            <button
                                type="button"
                                onClick={() => !done && setCheckpoint(c.value)}
                                disabled={done}
                                style={{
                                    all: 'unset', cursor: done ? 'default' : 'pointer', display: 'block',
                                    paddingBottom: isLast ? 0 : 10, flex: 1, textAlign: 'left',
                                    fontSize: 13, fontWeight: active ? 700 : 500,
                                    color: done ? C.textMuted : active ? C.text : C.textSub,
                                }}
                            >
                                {c.label}
                            </button>
                        </div>
                    );
                })}
            </div>

            <Btn variant="outline" onClick={submit} disabled={saving} style={{ fontSize: 12, padding: '6px 12px' }}>
                {saving ? 'Recording…' : `Record: ${SHREDDING_CHECKPOINTS.find(c => c.value === activeCheckpoint)?.label}`}
            </Btn>
            {success && <div style={{ fontSize: 12, color: C.greenMid, fontWeight: 600, marginTop: 4 }}>✓ {success}</div>}
            {error && <div style={{ fontSize: 11, color: '#C0392B', marginTop: 4 }}>{error}</div>}
        </div>
    );
}

// ── Active Job Card with Price Adjustment ─────────────────────────────────────

function ActiveJobCard({ job, onUpdateStatus, onJobCompletedLocally, onSendLocation, onGoToEstimates, onRequestEarlyStart }: {
    job: ContractorJob;
    onUpdateStatus: (id: string, s: string, actualUnits?: number) => void;
    onJobCompletedLocally: (id: string, actualUnits?: number) => void;
    onSendLocation: (id: string) => void;
    onGoToEstimates?: () => void;
    onRequestEarlyStart?: (id: string) => void;
}) {
    // Was comparing job.serviceType === 'Estimate Visit', but the backend only ever sends the
    // generic ServiceType enum ("General") here, never the descriptive ServiceTypeName string —
    // this check was silently always false, so a visit job never got routed into the estimate
    // builder here. originatingEstimateId alone isn't enough either: a CONVERTED estimate job
    // also sets it, but that's a real, priced job with line items to complete, not the free "go
    // build the estimate" visit — hasLineItems is what actually tells the two apart (mirrors the
    // same fix applied to mobile's App.tsx).
    const isEstimateVisit = !!job.originatingEstimateId && !job.hasLineItems;
    // Mirrors JobService.UpdateStatusAsync's server-side gate — a contractor could otherwise
    // mark a job En Route/Arrived/In Progress any time after accepting it, including well
    // before its scheduled date, with no approval required.
    const schedDate = job.scheduledDate ? new Date(job.scheduledDate) : null;
    const needsEarlyStartApproval = (() => {
        if (!schedDate || job.earlyStartStatus === 'Approved') return false;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const d = new Date(schedDate); d.setHours(0, 0, 0, 0);
        return d.getTime() > today.getTime();
    })();
    const [requestingEarlyStart, setRequestingEarlyStart] = useState(false);
    const requestEarlyStart = async () => {
        setRequestingEarlyStart(true);
        try { await onRequestEarlyStart?.(job.id); } finally { setRequestingEarlyStart(false); }
    };
    const chatEnabled = useFeature('inAppChat');
    const serviceCatalogEnabled = useFeature('serviceCatalogPricing');
    const partialPaymentsEnabled = useFeature('partialPayments');
    const inventoryTrackingEnabled = useFeature('inventoryTracking');
    const { serviceNoun } = useBranding();
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);
    const [showCatalogComplete, setShowCatalogComplete] = useState(false);
    const [completingCatalog, setCompletingCatalog] = useState(false);
    const [catalogCompleteError, setCatalogCompleteError] = useState('');
    const [showChat, setShowChat] = useState(false);
    const [actualUnits, setActualUnits] = useState(job.estimatedUnits.toString());
    const [adjustmentNote, setAdjustmentNote] = useState('');
    const [unitsError, setUnitsError] = useState('');
    const [loading, setLoading] = useState(false);
    const [waitingForCustomer, setWaitingForCustomer] = useState(job.priceAdjustmentStatus === 'Pending');
    const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [pinging, setPinging] = useState(false);
    const [lastPing, setLastPing] = useState<string | null>(null);
    const completingRef = useRef(false);

    useEffect(() => {
        if (job.status === 'EnRoute') {
            setPinging(true);
            onSendLocation(job.id);
            setLastPing(new Date().toLocaleTimeString());
            pingIntervalRef.current = setInterval(() => {
                onSendLocation(job.id);
                setLastPing(new Date().toLocaleTimeString());
            }, 5000);
        } else {
            setPinging(false);
            if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
        }
        return () => { if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; } };
    }, [job.status, job.id]);

    useEffect(() => { if (job.status === 'Completed') setWaitingForCustomer(false); }, [job.status]);

    const handleComplete = async () => {
        const units = parseInt(actualUnits);
        if (!units || units < 1) { setUnitsError('Please enter the actual number of units picked up.'); return; }
        if (completingRef.current) return;
        completingRef.current = true;
        setLoading(true); setUnitsError('');
        try {
            const token = localStorage.getItem('br_token');
            const res = await fetch(`${API_BASE}/api/price-adjustment/${job.id}/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ actualUnits: units, notes: adjustmentNote || null })
            });
            if (!res.ok) { const err = await res.json().catch(() => ({ error: 'Request failed' })); setUnitsError(err.error || 'Failed to submit'); return; }
            const data = await res.json();
            setShowCompleteDialog(false);
            // "Pending" means the price adjustment needs customer approval before anything
            // completes — otherwise this request already completed the job server-side (matching
            // or under-estimated units), so just reflect that locally rather than re-POSTing a
            // status change for a job that's already Completed.
            if (data.status === 'Pending') { setWaitingForCustomer(true); } else { onJobCompletedLocally(job.id, units); }
        } catch (e: any) { setUnitsError(e.message); }
        finally { completingRef.current = false; setLoading(false); }
    };

    // TREK-131 follow-up: line-item-priced (ServiceCatalogPricing) jobs skip the AI unit-count
    // flow entirely — that math doesn't apply and was corrupting the captured/payout amount.
    const completeCatalogJob = async () => {
        if (completingRef.current) return;
        completingRef.current = true;
        setCompletingCatalog(true); setCatalogCompleteError('');
        try {
            await api.completeLineItemJob(job.id);
            setShowCatalogComplete(false);
            // completeLineItemJob already set Job.Status = Completed server-side — same reasoning
            // as handleComplete above, reflect it locally instead of re-POSTing the status.
            onJobCompletedLocally(job.id);
        } catch (e: any) { setCatalogCompleteError(e.message); }
        finally { completingRef.current = false; setCompletingCatalog(false); }
    };

    const accuracyColor = () => {
        if (!job.actualUnits) return C.textMuted;
        const pct = Math.abs(job.actualUnits - job.estimatedUnits) / job.estimatedUnits * 100;
        if (pct <= 10) return C.greenMid; if (pct <= 25) return '#EF9F27'; return '#C0392B';
    };

    return (
        <>
            {showCompleteDialog && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <Card style={{ width: '100%', maxWidth: 380 }}>
                        <h3 style={{ margin: '0 0 6px' }}>✅ Complete pickup</h3>
                        <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 20px' }}>Enter the actual unit count. If more than estimated the customer will be asked to approve the adjusted price.</p>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 5 }}>Actual units picked up</label>
                            <input type="number" value={actualUnits} onChange={e => { setActualUnits(e.target.value); setUnitsError(''); }}
                                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${unitsError ? '#C0392B' : C.border}`, borderRadius: 8, fontSize: 18, fontWeight: 700, textAlign: 'center', boxSizing: 'border-box' as const }} />
                            {unitsError && <div style={{ fontSize: 12, color: '#C0392B', marginTop: 4 }}>{unitsError}</div>}
                        </div>
                        <div style={{ background: C.greenBg, borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, color: C.textSub }}>
                            Estimated at booking: <strong>{job.estimatedUnits} units</strong>.
                            {parseInt(actualUnits) > job.estimatedUnits && <span style={{ color: '#EF9F27', fontWeight: 600 }}> ⚠ More than estimated — customer approval required.</span>}
                        </div>
                        {parseInt(actualUnits) > job.estimatedUnits && (
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 5 }}>Note for customer (optional)</label>
                                <textarea value={adjustmentNote} onChange={e => setAdjustmentNote(e.target.value)} rows={2}
                                    placeholder="e.g. There were additional boxes in the garage..."
                                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'Inter, sans-serif', resize: 'none', boxSizing: 'border-box' as const }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={() => setShowCompleteDialog(false)}>Back</Btn>
                            <Btn onClick={handleComplete} disabled={loading} fullWidth size="lg">
                                {loading ? 'Submitting...' : parseInt(actualUnits) > job.estimatedUnits ? 'Request approval →' : 'Confirm & complete'}
                            </Btn>
                        </div>
                    </Card>
                </div>
            )}
            {showCatalogComplete && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <Card style={{ width: '100%', maxWidth: 380 }}>
                        <h3 style={{ margin: '0 0 6px' }}>✅ Complete job</h3>
                        <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 16px' }}>This job is priced by its line items — the customer will be charged the approved total.</p>
                        <div style={{ background: C.greenBg, borderRadius: 8, padding: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                            <span style={{ color: C.textSub }}>Total</span>
                            <span style={{ fontWeight: 700 }}>${job.totalPrice.toFixed(2)}</span>
                        </div>
                        {catalogCompleteError && <div style={{ fontSize: 12, color: '#C0392B', marginBottom: 12 }}>{catalogCompleteError}</div>}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={() => setShowCatalogComplete(false)}>Back</Btn>
                            <Btn onClick={completeCatalogJob} disabled={completingCatalog} fullWidth size="lg">
                                {completingCatalog ? 'Completing...' : 'Confirm & complete'}
                            </Btn>
                        </div>
                    </Card>
                </div>
            )}
            {waitingForCustomer && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <Card style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
                        <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
                        <h3 style={{ margin: '0 0 8px' }}>Waiting for customer</h3>
                        <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 20px' }}>The customer has been notified and needs to approve the adjusted price.</p>
                        <div style={{ background: C.greenBg, borderRadius: 8, padding: 12, marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>
                            <div><div style={{ fontSize: 11, color: C.textMuted }}>Estimated</div><div style={{ fontWeight: 700 }}>{job.estimatedUnits} units</div></div>
                            <div><div style={{ fontSize: 11, color: C.textMuted }}>Actual</div><div style={{ fontWeight: 700, color: '#EF9F27' }}>{actualUnits} units</div></div>
                            <div><div style={{ fontSize: 11, color: C.textMuted }}>Your Pay</div><div style={{ fontWeight: 700 }}>${payoutFor(job).toFixed(2)}</div></div>
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted }}>If the customer disputes this charge, an admin will review and resolve it.</div>
                    </Card>
                </div>
            )}
            <Card style={{ borderLeft: `4px solid ${C.greenMid}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                            <Badge label={job.status} />
                            <Badge label={job.serviceType} />
                            {job.priceAdjustmentStatus === 'Pending' && <span style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>⏳ Awaiting approval</span>}
                            {job.priceAdjustmentStatus === 'Disputed' && <span style={{ background: '#FDECEA', color: '#9B1C1C', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>⚠ Disputed</span>}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{job.pickupAddress}</div>
                        <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{job.estimatedUnits} est. units · ${payoutFor(job).toFixed(2)}</div>
                        {job.scheduledDate ? (
                            <div style={{ fontSize: 12, color: C.green, fontWeight: 600, marginTop: 4 }}>
                                📅 {new Date(job.scheduledDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{job.scheduledTimeSlot ? ` · ${job.scheduledTimeSlot}` : ''}
                            </div>
                        ) : job.requestedDate ? (
                            <div style={{ fontSize: 12, color: '#D97706', fontWeight: 600, marginTop: 4 }}>
                                📋 Requested: {new Date(job.requestedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </div>
                        ) : null}
                        {job.actualUnits && (
                            <div style={{ fontSize: 12, marginTop: 4, color: accuracyColor() }}>
                                ✓ Actual: {job.actualUnits} units ({job.actualUnits > job.estimatedUnits ? '+' : ''}{job.actualUnits - job.estimatedUnits} vs estimate)
                            </div>
                        )}
                        {job.donationCenter && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: C.greenBg, borderRadius: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: 0.5 }}>Deliver donation to</div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 2 }}>{job.donationCenter.name}</div>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.donationCenter.address)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: 12, color: C.greenMid, fontWeight: 600, textDecoration: 'none' }}
                                >
                                    {job.donationCenter.address} · Navigate →
                                </a>
                            </div>
                        )}
                        {job.shredding && job.status !== 'Completed' && (
                            <>
                                {/* Only a DestructionService visit actually destroys anything — a
                                    delivery or retrieval job has no declared categories to confirm. */}
                                {job.shredding.requestType === 'DestructionService' && <ShreddingCategoriesConfirm job={job} />}
                                <ShreddingBinScanPanel job={job} />
                            </>
                        )}
                        {pinging && (
                            <div style={{ fontSize: 12, color: C.greenMid, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.greenMid }} />
                                Auto-pinging · Last: {lastPing}
                            </div>
                        )}
                    </div>
                </div>
                {job.status === 'InProgress' && serviceCatalogEnabled && !isEstimateVisit && <JobPricingBuilder jobId={job.id} />}
                {job.status === 'InProgress' && inventoryTrackingEnabled && !isEstimateVisit && <JobInventoryUsagePanel jobId={job.id} />}
                {!['Completed', 'Cancelled'].includes(job.status) && partialPaymentsEnabled && !isEstimateVisit && (
                    <ContractorPaymentMilestonePanel jobId={job.id} jobTotal={job.totalPrice} />
                )}
                {isEstimateVisit && job.status === 'InProgress' && (
                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 10 }}>
                        Building estimate on-site — this completes automatically once the estimate is sent, approved, or declined.
                    </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {needsEarlyStartApproval && ['Accepted', 'EnRoute', 'Arrived'].includes(job.status) && (
                        job.earlyStartStatus === 'Requested' ? (
                            <Btn variant="outline" size="sm" disabled>⏳ Waiting for customer to approve early start</Btn>
                        ) : (
                            <Btn size="sm" onClick={requestEarlyStart} disabled={requestingEarlyStart}>
                                {`🕐 Scheduled ${schedDate!.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — Ask to start early`}
                            </Btn>
                        )
                    )}
                    {!needsEarlyStartApproval && job.status === 'Accepted' && <Btn onClick={() => onUpdateStatus(job.id, 'EnRoute')} size="sm">🚗 Start driving</Btn>}
                    {!needsEarlyStartApproval && job.status === 'EnRoute' && <Btn onClick={() => onUpdateStatus(job.id, 'Arrived')} size="sm">📍 Mark arrived</Btn>}
                    {!needsEarlyStartApproval && job.status === 'Arrived' && isEstimateVisit && (
                        <Btn onClick={() => { onUpdateStatus(job.id, 'InProgress'); onGoToEstimates?.(); }} size="sm">🧾 Start Estimate</Btn>
                    )}
                    {!needsEarlyStartApproval && job.status === 'Arrived' && !isEstimateVisit && <Btn onClick={() => onUpdateStatus(job.id, 'InProgress')} size="sm">📦 Start {serviceNoun ?? 'service'}</Btn>}
                    {job.status === 'InProgress' && isEstimateVisit && (
                        <Btn variant="outline" onClick={() => onGoToEstimates?.()} size="sm">🧾 Continue building estimate →</Btn>
                    )}
                    {job.status === 'InProgress' && !isEstimateVisit && !job.priceAdjustmentStatus && (
                        // hasLineItems, not just the tenant-wide flag — a job converted from an
                        // Estimate Builder estimate has real priced JobLineItems (and no
                        // meaningful "units") even on a tenant with ServiceCatalogPricing off.
                        (serviceCatalogEnabled || job.hasLineItems)
                            ? <Btn onClick={() => setShowCatalogComplete(true)} size="sm">✅ Mark complete</Btn>
                            : <Btn onClick={() => setShowCompleteDialog(true)} size="sm">✅ Mark complete</Btn>
                    )}
                    {job.priceAdjustmentStatus === 'Pending' && <Btn variant="outline" size="sm" onClick={() => setWaitingForCustomer(true)}>⏳ View approval status</Btn>}
                    {chatEnabled && <Btn variant="outline" size="sm" onClick={() => setShowChat(true)}>💬 Chat</Btn>}
                </div>
                {chatEnabled && showChat && <JobChat jobId={job.id} onClose={() => setShowChat(false)} />}
            </Card>
        </>
    );
}

// ── Payment milestones (TREK-80) — wireframe screen 4 ───────────────────────────
// Contractor/admin splits a job's total into deposit/progress/final milestones instead
// of one lump capture at completion. Once split, the single authorize-only intent (if
// any) is cancelled server-side — the two payment flows never overlap on the same job.

function ContractorPaymentMilestonePanel({ jobId, jobTotal }: { jobId: string; jobTotal: number }) {
    const [schedule, setSchedule] = useState<PaymentScheduleDto | null>(null);
    const [showSplit, setShowSplit] = useState(false);
    const [rows, setRows] = useState<{ label: string; amount: string; dueDescription: string }[]>([
        { label: 'Deposit', amount: '', dueDescription: '' },
        { label: 'Final', amount: '', dueDescription: 'Due on completion' },
    ]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const load = () => { api.getPaymentSchedule(jobId).then(setSchedule).catch(() => {}); };
    useEffect(() => { load(); }, [jobId]);

    const rowSum = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

    const addRow = () => setRows([...rows, { label: '', amount: '', dueDescription: '' }]);
    const updateRow = (i: number, field: 'label' | 'amount' | 'dueDescription', value: string) =>
        setRows(rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
    const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

    const save = async () => {
        setError('');
        if (Math.abs(rowSum - jobTotal) > 0.01) {
            setError(`Milestones must add up to the job total ($${jobTotal.toFixed(2)}) — currently $${rowSum.toFixed(2)}.`);
            return;
        }
        if (rows.some(r => !r.label.trim() || !r.amount)) {
            setError('Every milestone needs a label and an amount.');
            return;
        }
        setSaving(true);
        try {
            await api.splitJobIntoMilestones(jobId, rows.map(r => ({
                label: r.label.trim(), amount: parseFloat(r.amount), dueDescription: r.dueDescription.trim() || null,
            })));
            setShowSplit(false);
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    if (schedule && schedule.milestones.length > 0) {
        return (
            <Card style={{ marginTop: 10, background: C.bg }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Payment Schedule</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {schedule.milestones.map(m => (
                        <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span>{m.status === 'Captured' ? '✓ ' : ''}{m.label}{m.dueDescription && ` — ${m.dueDescription}`}</span>
                            <span style={{ fontWeight: 600 }}>${m.amount.toFixed(2)} · {m.status}</span>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    return (
        <div style={{ marginTop: 10 }}>
            {!showSplit ? (
                <Btn size="sm" variant="outline" onClick={() => setShowSplit(true)}>💰 Split into Payment Milestones</Btn>
            ) : (
                <Card style={{ background: C.bg }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        Split ${jobTotal.toFixed(2)} into milestones
                    </div>
                    <Alert message={error} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {rows.map((r, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input placeholder="Label (e.g. Deposit)" value={r.label} onChange={e => updateRow(i, 'label', e.target.value)}
                                    style={{ flex: 2, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                                <input placeholder="Amount" type="number" value={r.amount} onChange={e => updateRow(i, 'amount', e.target.value)}
                                    style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                                <input placeholder="Due on... (optional)" value={r.dueDescription} onChange={e => updateRow(i, 'dueDescription', e.target.value)}
                                    style={{ flex: 2, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                                {rows.length > 1 && <Btn size="sm" variant="danger" onClick={() => removeRow(i)}>✕</Btn>}
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 12, color: rowSum === jobTotal ? C.greenMid : C.textSub, marginTop: 8 }}>
                        Total: ${rowSum.toFixed(2)} / ${jobTotal.toFixed(2)}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <Btn size="sm" variant="outline" onClick={addRow}>+ Add milestone</Btn>
                        <Btn size="sm" variant="outline" onClick={() => setShowSplit(false)}>Cancel</Btn>
                        <Btn size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Schedule'}</Btn>
                    </div>
                </Card>
            )}
        </div>
    );
}

// ── Job inventory usage (TREK-81) ────────────────────────────────────────────────
// Contractor logs parts/supplies used on this job — deducts stock in real time.

function JobInventoryUsagePanel({ jobId }: { jobId: string }) {
    const [items, setItems] = useState<InventoryItemDto[]>([]);
    const [usage, setUsage] = useState<InventoryUsageDto[]>([]);
    const [selectedItemId, setSelectedItemId] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const load = () => {
        api.getInventory().then(setItems).catch(() => {});
        api.getJobInventoryUsage(jobId).then(setUsage).catch(() => {});
    };
    useEffect(() => { load(); }, [jobId]);

    const logUsage = async () => {
        setError('');
        if (!selectedItemId) { setError('Select a part or supply.'); return; }
        const qty = parseInt(quantity, 10);
        if (isNaN(qty) || qty <= 0) { setError('Enter a valid quantity.'); return; }
        setSaving(true);
        try {
            await api.logJobInventoryUsage(jobId, selectedItemId, qty);
            setSelectedItemId(''); setQuantity('1');
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ margin: '12px 0', padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.borderLight}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Parts used
            </div>
            <Alert message={error} />
            {usage.map(u => (
                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px dashed ${C.borderLight}`, fontSize: 13 }}>
                    <span>{u.inventoryItemName}</span>
                    <span style={{ fontWeight: 600 }}>{Math.abs(u.quantityChange)}</span>
                </div>
            ))}
            {usage.length === 0 && <div style={{ fontSize: 12, color: C.textMuted, padding: '6px 0' }}>No parts logged yet.</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <select value={selectedItemId} onChange={e => setSelectedItemId(e.target.value)}
                    style={{ flex: 2, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }}>
                    <option value="">— Select part/supply —</option>
                    {items.map(i => <option key={i.id} value={i.id}>{i.name} ({i.quantityOnHand} on hand)</option>)}
                </select>
                <input value={quantity} onChange={e => setQuantity(e.target.value)} type="number" min="1"
                    style={{ width: 60, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                <Btn size="sm" onClick={logUsage} disabled={saving}>{saving ? '...' : '+ Log'}</Btn>
            </div>
        </div>
    );
}

// ── Job pricing builder (TREK-132) — wireframe screen 2 ────────────────────────
// Contractor builds the job total from catalog items/parts instead of one flat price.
// Same underlying Job entity as BoxRunner — this is an alternate pricing path, not a parallel job system.

function JobPricingBuilder({ jobId }: { jobId: string }) {
    const estimatesEnabled = useFeature('estimatesAndQuotes');
    const [catalog, setCatalog] = useState<ServiceCatalogItemDto[]>([]);
    const [lineItems, setLineItems] = useState<JobLineItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [estimate, setEstimate] = useState<EstimateDto | null>(null);
    const [sendingEstimate, setSendingEstimate] = useState(false);

    const [showAdd, setShowAdd] = useState(false);
    const [selectedCatalogId, setSelectedCatalogId] = useState('');
    const [customDescription, setCustomDescription] = useState('');
    const [customPrice, setCustomPrice] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [afterHours, setAfterHours] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true); setError('');
        const calls: [Promise<ServiceCatalogItemDto[]>, Promise<any>, Promise<EstimateDto | null>] =
            [api.getServiceCatalog(), api.getJobLineItems(jobId), estimatesEnabled ? api.getEstimate(jobId) : Promise.resolve(null)];
        Promise.all(calls)
            .then(([items, summary, est]) => {
                setCatalog(items);
                setLineItems(summary.lineItems);
                setTotal(summary.total);
                setEstimate(est);
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [jobId]);

    const sendEstimate = async () => {
        setSendingEstimate(true); setError('');
        try {
            await api.sendEstimate(jobId);
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSendingEstimate(false); }
    };

    const addItem = async () => {
        setSaving(true); setError('');
        try {
            const qty = parseFloat(quantity);
            if (isNaN(qty) || qty <= 0) { setError('Enter a valid quantity.'); return; }
            if (selectedCatalogId) {
                await api.addJobLineItem(jobId, selectedCatalogId, null, qty, null, afterHours);
            } else {
                if (!customDescription.trim() || !customPrice) { setError('Description and price are required for a custom part.'); return; }
                await api.addJobLineItem(jobId, null, customDescription.trim(), qty, parseFloat(customPrice), afterHours);
            }
            setShowAdd(false);
            setSelectedCatalogId(''); setCustomDescription(''); setCustomPrice(''); setQuantity('1'); setAfterHours(false);
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const removeItem = async (lineItemId: string) => {
        setError('');
        try { await api.removeJobLineItem(jobId, lineItemId); load(); }
        catch (e: any) { setError(e.message); }
    };

    if (loading) return null;

    const locked = estimate?.status === 'Pending';

    return (
        <div style={{ margin: '12px 0', padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.borderLight}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Job pricing
            </div>
            <Alert message={error} />

            {estimate?.status === 'Pending' && (
                <div style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    ⏳ Estimate #E-{estimate.number} sent — awaiting customer approval
                </div>
            )}
            {estimate?.status === 'ChangesRequested' && (
                <div style={{ background: '#FDECEA', color: '#9B1C1C', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    ⚠ Customer requested changes to Estimate #E-{estimate.number}{estimate.customerNote ? `: "${estimate.customerNote}"` : ''}
                </div>
            )}
            {estimate?.status === 'Approved' && (
                <div style={{ background: C.greenBg, color: C.green, borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    ✓ Estimate #E-{estimate.number} approved — cleared to complete the job
                </div>
            )}

            {lineItems.map(li => (
                <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px dashed ${C.borderLight}`, fontSize: 13 }}>
                    <span>{li.description}{li.quantity !== 1 ? ` × ${li.quantity}` : ''}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600 }}>${li.lineTotal.toFixed(2)}</span>
                        {!locked && (
                            <button onClick={() => removeItem(li.id)} title="Remove"
                                style={{ background: 'none', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                        )}
                    </div>
                </div>
            ))}
            {lineItems.length === 0 && <div style={{ fontSize: 12, color: C.textMuted, padding: '6px 0' }}>No items yet.</div>}

            {!locked && showAdd ? (
                <div style={{ marginTop: 10, padding: 10, background: '#fff', borderRadius: 6, border: `1px solid ${C.border}` }}>
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 4 }}>Catalog item</label>
                        <select value={selectedCatalogId} onChange={e => setSelectedCatalogId(e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }}>
                            <option value="">— Custom part (not in catalog) —</option>
                            {catalog.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name} · {c.pricingType === 'Hourly' ? 'hourly' : `$${c.price.toFixed(2)}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    {!selectedCatalogId && (
                        <>
                            <div style={{ marginBottom: 8 }}>
                                <Input label="Description" value={customDescription} onChange={setCustomDescription} placeholder="e.g. Capacitor" />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                <Input label="Unit price ($)" value={customPrice} onChange={setCustomPrice} type="number" placeholder="48.00" />
                            </div>
                        </>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                            <Input label={selectedCatalogId && catalog.find(c => c.id === selectedCatalogId)?.pricingType === 'Hourly' ? 'Hours' : 'Quantity'} value={quantity} onChange={setQuantity} type="number" />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={afterHours} onChange={e => setAfterHours(e.target.checked)} style={{ accentColor: C.greenMid }} />
                        <span style={{ fontSize: 12, color: C.text }}>After-hours / emergency rate</span>
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn>
                        <Btn size="sm" onClick={addItem} disabled={saving}>{saving ? 'Adding...' : 'Add'}</Btn>
                    </div>
                </div>
            ) : !locked ? (
                <Btn size="sm" variant="outline" onClick={() => setShowAdd(true)} style={{ width: '100%', marginTop: 8 }}>+ Add Item / Part</Btn>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14 }}>
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
            </div>

            {estimatesEnabled && !locked && estimate?.status !== 'Approved' && lineItems.length > 0 && (
                <Btn onClick={sendEstimate} disabled={sendingEstimate} style={{ width: '100%', marginTop: 10, padding: 11 }}>
                    {sendingEstimate ? 'Sending...' : 'Send Estimate to Customer'}
                </Btn>
            )}
        </div>
    );
}

// ── Background check banner helpers ──────────────────────────────────────────

function getBgCheckBannerStyle(status: string): React.CSSProperties {
    switch (status) {
        case 'Consider':
        case 'Disputed':  return { background: '#FFF7E6', borderColor: '#EF9F27', color: '#854F0B' };
        case 'Suspended': return { background: '#FDECEA', borderColor: C.coral, color: '#9B1C1C' };
        default:          return { background: '#EEF2FF', borderColor: '#6366F1', color: '#3730A3' };
    }
}
function getBgCheckBannerIcon(status: string) {
    switch (status) {
        case 'Pending': return '🛡'; case 'Invited': return '📧';
        case 'CheckrPending': return '⏳'; case 'Consider': return '⚠️';
        case 'Disputed': return '⚖️'; case 'Suspended': return '🚫';
        default: return '🛡';
    }
}
function getBgCheckBannerTitle(status: string) {
    switch (status) {
        case 'Pending': return 'Background check required';
        case 'Invited': return 'Complete your background check';
        case 'CheckrPending': return 'Background check in progress';
        case 'Consider': return 'Background check under review';
        case 'Disputed': return 'Background check disputed';
        case 'Suspended': return 'Account suspended';
        default: return 'Background check required';
    }
}
function getBgCheckBannerMessage(status: string) {
    switch (status) {
        case 'Pending': return 'You need to complete a background check before accepting jobs.';
        case 'Invited': return 'You have a pending invitation. Click to complete your background check.';
        case 'CheckrPending': return 'Your background check is running. This typically takes 24-48 hours.';
        case 'Consider': return 'A flagged item was found. An admin is reviewing your report.';
        case 'Disputed': return "Your dispute is being reviewed. We'll notify you of the outcome.";
        case 'Suspended': return 'Your account has been suspended. Please contact support.';
        default: return 'Please complete your background check to accept jobs.';
    }
}

// ── Earnings chart helpers ─────────────────────────────────────────────────────

function EarningsTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            {label && <div style={{ color: '#898781', marginBottom: 4 }}>{label}</div>}
            {payload.map((p: any, i: number) => (
                <div key={i} style={{ color: p.color, fontWeight: 600 }}>
                    {p.name}: ${typeof p.value === 'number' ? p.value.toFixed(2) : p.value}
                </div>
            ))}
        </div>
    );
}

function EarningsSparkCard({ title, value, sub, data, color }: { title: string; value: string; sub?: string; data: { v: number }[]; color: string }) {
    return (
        <div style={{ background: C.card, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8 }}>
                <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>{value}</div>
                    {sub && <div style={{ fontSize: 11, color: C.textSub, marginTop: 3 }}>{sub}</div>}
                </div>
                <div style={{ width: 70, height: 32, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data}>
                            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

// ── Earnings View ─────────────────────────────────────────────────────────────

function EarningsView({ completedLoadingJobs = [] }: { completedLoadingJobs?: any[] }) {
    const { user } = useAuth();
    const branding = useBranding();
    const [status, setStatus] = useState<ConnectStatusResponse | null>(null);
    const [history, setHistory] = useState<PayoutHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(false);
    const [retryResult, setRetryResult] = useState<RetryPayoutsResult | null>(null);
    const [error, setError] = useState('');

    const fetchAll = React.useCallback(async () => {
        try {
            const [s, h] = await Promise.all([api.getPayoutStatus(), api.getPayoutHistory()]);
            setStatus(s); setHistory(h);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const startOnboarding = async () => {
        try {
            const res = await api.startOnboarding(`${window.location.origin}/contractor`, `${window.location.origin}/contractor`);
            window.open(res.url, '_blank');
        } catch (e: any) { setError(e.message); }
    };

    const refreshStatus = async () => {
        try { const s = await api.refreshOnboardingStatus(); setStatus(s); }
        catch (e: any) { setError(e.message); }
    };

    const retryAll = async () => {
        setRetrying(true); setRetryResult(null);
        try { const r = await api.retryPendingPayouts(); setRetryResult(r); await fetchAll(); }
        catch (e: any) { setError(e.message); }
        finally { setRetrying(false); }
    };

    const payoutStatusStyle = (s: string): React.CSSProperties => {
        switch (s) {
            case 'Transferred': return { background: '#E1F5EE', color: '#085041' };
            case 'Failed': return { background: '#FDECEA', color: '#9B1C1C' };
            case 'Skipped': return { background: '#FFF7E6', color: '#854F0B' };
            default: return { background: '#F5F5F5', color: '#666' };
        }
    };

    // ── Chart derivations ──
    const now = new Date();
    const allEarnings = [
        ...history.map(h => ({ date: new Date(h.completedAt), earned: h.contractorPayout, type: h.serviceType, transferred: h.payoutStatus === 'Transferred' })),
        ...completedLoadingJobs.map((j: any) => ({ date: new Date(j.completedAt || j.createdAt || Date.now()), earned: (j.finalCost ?? 0) * 0.8, type: 'Loading', transferred: true })),
    ];

    const weekAgo = new Date(now.getTime() - 7 * 86400000);
    const monthAgo = new Date(now.getTime() - 30 * 86400000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 86400000);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    const thisWeekTotal = allEarnings.filter(e => e.date >= weekAgo).reduce((s, e) => s + e.earned, 0);
    const thisMonthTotal = allEarnings.filter(e => e.date >= monthAgo).reduce((s, e) => s + e.earned, 0);
    const ytdTotal = allEarnings.filter(e => e.date >= yearStart).reduce((s, e) => s + e.earned, 0);
    const lastWeekTotal = allEarnings.filter(e => e.date >= twoWeeksAgo && e.date < weekAgo).reduce((s, e) => s + e.earned, 0);
    const weekTrend = lastWeekTotal === 0 ? null : ((thisWeekTotal - lastWeekTotal) / lastWeekTotal) * 100;

    // 12-week area chart
    const weeklyChartData = Array.from({ length: 12 }, (_, i) => {
        const wEnd = new Date(now.getTime() - i * 7 * 86400000);
        const wStart = new Date(now.getTime() - (i + 1) * 7 * 86400000);
        const earned = allEarnings.filter(e => e.date >= wStart && e.date < wEnd).reduce((s, e) => s + e.earned, 0);
        return { label: wEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), earnings: earned };
    }).reverse();

    // Service type donut
    const PIE_COLORS = ['#2a78d6', '#1baf7a', '#eda100', '#4a3aa7', '#e34948'];
    const stEarnings = allEarnings.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + e.earned; return acc; }, {} as Record<string, number>);
    const stEntries = Object.entries(stEarnings).sort(([, a], [, b]) => b - a);
    const pieData = stEntries.length === 0 ? [] :
        stEntries.length <= 5
            ? stEntries.map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
            : [...stEntries.slice(0, 4).map(([name, value]) => ({ name, value: Number(value.toFixed(2)) })), { name: 'Other', value: Number(stEntries.slice(4).reduce((s, [, v]) => s + v, 0).toFixed(2)) }];

    // Sparklines (8 weeks)
    const sparkData = Array.from({ length: 8 }, (_, i) => {
        const wEnd = new Date(now.getTime() - i * 7 * 86400000);
        const wStart = new Date(now.getTime() - (i + 1) * 7 * 86400000);
        const wItems = allEarnings.filter(e => e.date >= wStart && e.date < wEnd);
        const wEarned = wItems.reduce((s, e) => s + e.earned, 0);
        return { count: wItems.length, avg: wItems.length ? wEarned / wItems.length : 0, earned: wEarned };
    }).reverse();

    const totalJobs = allEarnings.length;
    const avgPerJob = totalJobs > 0 ? allEarnings.reduce((s, e) => s + e.earned, 0) / totalJobs : 0;
    const jobsThisMonth = allEarnings.filter(e => e.date >= monthAgo).length;

    const pendingCount = history.filter(h => ['Pending', 'Skipped', 'Failed'].includes(h.payoutStatus)).length;
    if (loading) return <Spinner />;

    return (
        <div>
            <Alert message={error} />

            {/* Welcome strip */}
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Your Earnings{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h2>
            </div>

            {/* KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
                {[
                    { label: 'This Week', value: `$${thisWeekTotal.toFixed(2)}`, trend: weekTrend },
                    { label: 'This Month', value: `$${thisMonthTotal.toFixed(2)}`, trend: null },
                    { label: 'Year to Date', value: `$${ytdTotal.toFixed(2)}`, trend: null },
                    { label: 'Pending Payout', value: `$${(status?.pendingPayout ?? 0).toFixed(2)}`, trend: null, warn: (status?.pendingPayout ?? 0) > 0 },
                ].map(k => (
                    <div key={k.label} style={{ background: C.card, borderRadius: 12, padding: '16px 18px', border: `1px solid ${(k as any).warn ? '#EF9F27' : C.border}` }}>
                        <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{k.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: C.greenMid, lineHeight: 1 }}>{k.value}</div>
                        {k.trend !== null && k.trend !== undefined && (
                            <div style={{ fontSize: 12, marginTop: 5, color: k.trend >= 0 ? '#006300' : '#e34948', fontWeight: 600 }}>
                                {k.trend >= 0 ? '↑' : '↓'} {Math.abs(k.trend).toFixed(1)}% vs last week
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 20 }}>
                <Card style={{ padding: '14px 16px 6px' }}>
                    <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Weekly Earnings (12 weeks)</div>
                    <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={weeklyChartData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gEarnings" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#1baf7a" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#1baf7a" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#e1e0d9" strokeWidth={0.5} vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#898781' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 10, fill: '#898781' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `$${v}`} />
                            <Tooltip content={<EarningsTooltip />} />
                            <Area type="monotone" dataKey="earnings" name="Earnings" stroke="#1baf7a" strokeWidth={2} fill="url(#gEarnings)" dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                {/* Service type donut */}
                <Card style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>By Service Type</div>
                    {pieData.length === 0 ? (
                        <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: 13 }}>No data yet</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={pieData} cx="50%" cy="42%" innerRadius={48} outerRadius={75} paddingAngle={2} dataKey="value" nameKey="name">
                                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip content={<EarningsTooltip />} />
                                <Legend iconType="circle" iconSize={8} formatter={(v: string) => <span style={{ fontSize: 10, color: C.textSub }}>{v}</span>} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </Card>
            </div>

            {/* Sparklines */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
                <EarningsSparkCard title="Avg per Job" value={`$${avgPerJob.toFixed(0)}`} sub="lifetime average" data={sparkData.map(w => ({ v: w.avg }))} color="#2a78d6" />
                <EarningsSparkCard title="Jobs This Month" value={String(jobsThisMonth)} sub="last 30 days" data={sparkData.map(w => ({ v: w.count }))} color="#1baf7a" />
                <EarningsSparkCard title="Total Jobs" value={String(totalJobs)} sub="all time" data={sparkData.map(w => ({ v: w.earned }))} color="#4a3aa7" />
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                <StatCard label="Total earned" value={`$${((status?.totalEarned ?? 0) + completedLoadingJobs.reduce((s: number, j: any) => s + (j.finalCost ?? 0) * 0.8, 0)).toFixed(2)}`} color={C.greenMid} />
                <StatCard label="Pending payout" value={`$${(status?.pendingPayout ?? 0).toFixed(2)}`} />
                <StatCard label="Jobs completed" value={(status?.completedJobs ?? 0) + completedLoadingJobs.length} />
            </div>
            {!status?.onboardingComplete ? (
                <Card style={{ borderLeft: `4px solid ${C.yellow}`, marginBottom: 24 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>💳 Set up payouts</div>
                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16, lineHeight: 1.6 }}>Connect your bank account through Stripe to receive automatic payouts.</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn onClick={startOnboarding}>{status?.hasAccount ? 'Continue setup →' : 'Connect bank account →'}</Btn>
                        {status?.hasAccount && <Btn variant="outline" onClick={refreshStatus}>I finished setup — check status</Btn>}
                    </div>
                </Card>
            ) : (
                <Card style={{ borderLeft: `4px solid ${C.greenMid}`, marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ fontSize: 32 }}>✅</div>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>Bank account connected</div>
                            <div style={{ fontSize: 13, color: C.textSub }}>Payouts sent automatically</div>
                        </div>
                    </div>
                </Card>
            )}
            {pendingCount > 0 && status?.onboardingComplete && (
                <Card style={{ borderLeft: `4px solid ${C.yellow}`, marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠ {pendingCount} payout{pendingCount > 1 ? 's' : ''} pending</div>
                            <div style={{ fontSize: 13, color: C.textSub }}>These jobs completed before your bank account was connected.</div>
                        </div>
                        <Btn onClick={retryAll} disabled={retrying}>{retrying ? 'Retrying...' : `Retry ${pendingCount} payout${pendingCount > 1 ? 's' : ''}`}</Btn>
                    </div>
                    {retryResult && (
                        <div style={{ marginTop: 16, padding: 12, background: retryResult.failed === 0 ? C.greenBg : '#FFF7E6', borderRadius: 8 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{retryResult.succeeded}/{retryResult.attempted} succeeded · ${retryResult.totalTransferred.toFixed(2)} transferred</div>
                            {retryResult.errors.map((e: string, i: number) => <div key={i} style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>• {e}</div>)}
                        </div>
                    )}
                </Card>
            )}
            {history.length === 0 && completedLoadingJobs.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40 }}>💸</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No payouts yet</div>
                </Card>
            ) : (
                <>
                    {history.length > 0 && (
                        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                                        {['Date', 'Job', 'Your cut', 'Status'].map(h => (
                                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((item: PayoutHistoryItem, i: number) => (
                                        <tr key={item.jobId} style={{ borderBottom: i < history.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                                            <td style={{ padding: '12px 16px', color: C.textSub }}>{new Date(item.completedAt).toLocaleDateString()}</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <div style={{ fontWeight: 500 }}>{item.pickupAddress}</div>
                                                <div style={{ fontSize: 11, color: C.textMuted }}>{item.serviceType}</div>
                                            </td>
                                            <td style={{ padding: '12px 16px', fontWeight: 700, color: item.payoutStatus === 'Transferred' ? C.greenMid : C.textMuted }}>
                                                {item.contractorPayout > 0 ? `$${item.contractorPayout.toFixed(2)}` : '—'}
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{ ...payoutStatusStyle(item.payoutStatus), borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                                                    {item.payoutStatus}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg }}>
                                        <td colSpan={2} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: C.textSub }}>Total transferred</td>
                                        <td style={{ padding: '10px 16px', fontWeight: 700, color: C.greenMid, fontSize: 15 }}>
                                            ${history.filter((h: PayoutHistoryItem) => h.payoutStatus === 'Transferred').reduce((s: number, h: PayoutHistoryItem) => s + h.contractorPayout, 0).toFixed(2)}
                                        </td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </Card>
                    )}
                    {completedLoadingJobs.length > 0 && (
                        <Card style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ padding: '10px 16px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Dumpster Loading Jobs
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                                        {['Date', 'Address', 'Hours', 'Your cut', 'Status'].map(h => (
                                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {completedLoadingJobs.map((job: any, i: number) => (
                                        <tr key={job.id} style={{ borderBottom: i < completedLoadingJobs.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                                            <td style={{ padding: '12px 16px', color: C.textSub }}>{job.completedAt ? new Date(job.completedAt).toLocaleDateString() : '—'}</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <div style={{ fontWeight: 500 }}>{job.deliveryAddress ?? '—'}</div>
                                                <div style={{ fontSize: 11, color: C.textMuted }}>Dumpster loading</div>
                                            </td>
                                            <td style={{ padding: '12px 16px', color: C.textSub }}>{job.hoursWorked != null ? `${Number(job.hoursWorked).toFixed(2)}h` : '—'}</td>
                                            <td style={{ padding: '12px 16px', fontWeight: 700, color: C.greenMid }}>
                                                {job.finalCost != null ? `$${(job.finalCost * 0.8).toFixed(2)}` : '—'}
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{ background: '#E1F5EE', color: '#085041', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                                                    Approved
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg }}>
                                        <td colSpan={3} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: C.textSub }}>Total loading earnings</td>
                                        <td style={{ padding: '10px 16px', fontWeight: 700, color: C.greenMid, fontSize: 15 }}>
                                            ${completedLoadingJobs.reduce((s: number, j: any) => s + (j.finalCost ?? 0) * 0.8, 0).toFixed(2)}
                                        </td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}

// ── Tier View ─────────────────────────────────────────────────────────────────

function TierView() {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const [stats, setStats] = useState<ContractorTierStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getMyTierStats().then(setStats).catch(console.error).finally(() => setLoading(false));
    }, []);

    if (loading) return <Spinner />;
    if (!stats) return <div style={{ color: C.textMuted, textAlign: 'center', padding: 48 }}>Could not load tier stats.</div>;

    const tierBg = stats.tier === 'Master' ? '#FEF3C7' : stats.tier === 'Journeyman' ? '#EEF2FF' : C.bg;

    return (
        <div>
            <Card style={{ background: tierBg, borderLeft: `4px solid ${stats.tierColor}`, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 48 }}>{stats.tierIcon}</div>
                    <div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: stats.tierColor }}>{stats.tier} {contractorLabel}</div>
                        <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>{stats.jobCount} jobs · ★ {stats.averageRating.toFixed(1)}</div>
                        {stats.tierAchievedAt && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Since {new Date(stats.tierAchievedAt).toLocaleDateString()}</div>}
                    </div>
                </div>
                {stats.tier !== 'Master' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.textSub, marginBottom: 6 }}>
                            <span>Progress to {stats.tier === 'Apprentice' ? 'Journeyman' : 'Master'}</span>
                            <span>{stats.tierProgressPercent}%</span>
                        </div>
                        <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${stats.tierProgressPercent}%`, background: stats.tierColor, borderRadius: 4 }} />
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>
                            {stats.jobsToNextTier && stats.jobsToNextTier > 0 ? `${stats.jobsToNextTier} more jobs needed` : ''}
                            {stats.ratingForNextTier && stats.averageRating < stats.ratingForNextTier ? ` · ${stats.ratingForNextTier}+ rating required` : ''}
                        </div>
                    </div>
                )}
            </Card>
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                <StatCard label="Current streak" value={`🔥 ${stats.currentStreak}`} sub="jobs in a row" />
                <StatCard label="Best streak" value={`⚡ ${stats.longestStreak}`} sub="all time" />
                <StatCard label="Total jobs" value={stats.jobCount} />
            </div>
            <Card>
                <h3 style={{ margin: '0 0 16px', fontSize: 14, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Achievements ({stats.achievements.filter((a: AchievementItem) => a.earned).length}/{stats.achievements.length})
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                    {stats.achievements.map((a: AchievementItem) => (
                        <div key={a.key} style={{ padding: 12, borderRadius: 10, border: `1px solid ${a.earned ? C.greenMid : C.border}`, background: a.earned ? C.greenBg : C.bg, opacity: a.earned ? 1 : 0.5 }}>
                            <div style={{ fontSize: 24, marginBottom: 4 }}>{a.icon}</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: a.earned ? C.green : C.textSub }}>{a.name}</div>
                            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{a.description}</div>
                            {a.earned && <div style={{ fontSize: 10, color: C.greenMid, marginTop: 4, fontWeight: 600 }}>✓ EARNED</div>}
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}

// ── Job Chat ──────────────────────────────────────────────────────────────────

function JobChat({ jobId, onClose }: { jobId: string; onClose: () => void }) {
    const { user } = useAuth();
    const [messages, setMessages] = useState<MessageItem[]>([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const hubRef = React.useRef<signalR.HubConnection | null>(null);

    useEffect(() => {
        api.getMessages(jobId).then(msgs => { setMessages(msgs); setLoading(false); }).catch(() => setLoading(false));
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect().build();
        hub.on('NewMessage', (msg: MessageItem) => {
            if (msg.jobId === jobId) setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        });
        hub.start().then(() => hub.invoke('SubscribeToJob', jobId).catch(() => {})).catch(console.error);
        hubRef.current = hub;
        return () => { hub.stop(); };
    }, [jobId]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const send = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await api.sendMessage(jobId, text.trim());
            setText('');
        } catch (e: any) { alert(e.message); }
        finally { setSending(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ width: '100%', maxWidth: 500, background: '#fff', borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>💬 Job Chat</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.textMuted }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {loading ? <Spinner /> : messages.length === 0
                        ? <div style={{ textAlign: 'center', color: C.textMuted, padding: 32 }}>No messages yet. Start the conversation!</div>
                        : messages.map((m: MessageItem) => {
                            const isMe = m.senderId === user?.userId;
                            return (
                                <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                    <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px', background: isMe ? C.greenMid : C.bg, color: isMe ? '#fff' : C.text }}>
                                        {!isMe && <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: C.textSub }}>{m.senderRole}</div>}
                                        <div style={{ fontSize: 14 }}>{m.text}</div>
                                        <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: 'right' }}>
                                            {new Date(m.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                    <div ref={bottomRef} />
                </div>
                <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10 }}>
                    <input value={text} onChange={e => setText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                        placeholder="Type a message..."
                        style={{ flex: 1, padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 24, fontSize: 14, outline: 'none' }} />
                    <Btn onClick={send} disabled={!text.trim() || sending} size="sm">{sending ? '…' : 'Send'}</Btn>
                </div>
            </div>
        </div>
    );
}

// ── Onboarding View ───────────────────────────────────────────────────────────

function OnboardingView({ docStatus, onStatusChange }: { docStatus: any; onStatusChange: (s: any) => void }) {
    const [step, setStep] = useState<'license' | 'insurance' | 'vehicle' | 'done'>('license');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [licNumber, setLicNumber] = useState('');
    const [licExpiry, setLicExpiry] = useState('');
    const [licPhoto, setLicPhoto] = useState<string | null>(null);
    const [licPhotoName, setLicPhotoName] = useState('');

    const [insProvider, setInsProvider] = useState('');
    const [insPolicy, setInsPolicy] = useState('');
    const [insExpiry, setInsExpiry] = useState('');
    const [insPhoto, setInsPhoto] = useState<string | null>(null);
    const [insPhotoName, setInsPhotoName] = useState('');

    const [vehMake, setVehMake] = useState('');
    const [vehModel, setVehModel] = useState('');
    const [vehYear, setVehYear] = useState('');
    const [vehColor, setVehColor] = useState('');
    const [vehPlate, setVehPlate] = useState('');
    const [vehPhoto, setVehPhoto] = useState<string | null>(null);
    const [vehPhotoName, setVehPhotoName] = useState('');

    React.useEffect(() => {
        if (!docStatus) return;
        // Only auto-advance if not rejected/expired (allow resubmit from any step)
        const licOk = !['Pending', 'Rejected', 'Expired'].includes(docStatus.driversLicenseStatus);
        const insOk = !['Pending', 'Rejected', 'Expired'].includes(docStatus.insuranceStatus);
        const vehOk = !['Pending', 'Rejected', 'Expired'].includes(docStatus.vehicleStatus);
        if (licOk) setStep('insurance');
        if (licOk && insOk) setStep('vehicle');
        if (licOk && insOk && vehOk) setStep('done');
    }, [docStatus]);

    const handlePhoto = (file: File, setter: (b: string) => void, nameSetter: (n: string) => void) => {
        const reader = new FileReader();
        reader.onload = e => { setter(e.target?.result as string); nameSetter(file.name); };
        reader.readAsDataURL(file);
    };

    const submitLicense = async () => {
        if (!licNumber || !licExpiry || !licPhoto) { setError('Please fill all fields and upload a photo.'); return; }
        setLoading(true); setError('');
        try {
            const res = await api.submitLicense(licNumber, licExpiry, licPhoto);
            onStatusChange(res);
            setSuccess("Driver's license submitted!");
            setTimeout(() => { setSuccess(''); setStep('insurance'); }, 1500);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const submitInsurance = async () => {
        if (!insProvider || !insPolicy || !insExpiry || !insPhoto) { setError('Please fill all fields and upload a photo.'); return; }
        setLoading(true); setError('');
        try {
            const res = await api.submitInsurance(insProvider, insPolicy, insExpiry, insPhoto);
            onStatusChange(res);
            setSuccess('Insurance submitted!');
            setTimeout(() => { setSuccess(''); setStep('vehicle'); }, 1500);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const submitVehicle = async () => {
        if (!vehMake || !vehModel || !vehYear || !vehColor || !vehPlate) { setError('Please fill all required fields.'); return; }
        setLoading(true); setError('');
        try {
            const res = await api.submitVehicle(vehMake, vehModel, parseInt(vehYear), vehColor, vehPlate, vehPhoto || undefined);
            onStatusChange(res);
            setSuccess('Vehicle info submitted!');
            setTimeout(() => { setSuccess(''); setStep('done'); }, 1500);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const steps = [
        { key: 'license', label: '🪪 License', done: docStatus?.driversLicenseStatus !== 'Pending' },
        { key: 'insurance', label: '📄 Insurance', done: docStatus?.insuranceStatus !== 'Pending' },
        { key: 'vehicle', label: '🚗 Vehicle', done: docStatus?.vehicleStatus !== 'Pending' },
    ];

    const docStatusBadge = (status: string) => {
        switch (status) {
            case 'Approved': return <span style={{ color: C.green, fontWeight: 600, fontSize: 12 }}>✓ Approved</span>;
            case 'Submitted': return <span style={{ color: '#3730A3', fontWeight: 600, fontSize: 12 }}>⏳ Under review</span>;
            case 'Rejected': return <span style={{ color: C.coral, fontWeight: 600, fontSize: 12 }}>✗ Rejected — resubmit</span>;
            case 'Expired': return <span style={{ color: '#854F0B', fontWeight: 600, fontSize: 12 }}>⚠️ Expired — renew</span>;
            default: return <span style={{ color: C.textMuted, fontWeight: 600, fontSize: 12 }}>○ Not submitted</span>;
        }
    };

    return (
        <div>
            <Alert message={error} />
            {success && <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.green, fontWeight: 600 }}>✓ {success}</div>}

            {/* Progress steps */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 28 }}>
                {steps.map((s, i) => (
                    <div key={s.key} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} onClick={() => setStep(s.key as any)}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, background: s.done ? C.greenMid : step === s.key ? C.green : C.border, color: s.done || step === s.key ? '#fff' : C.textSub, marginBottom: 6 }}>
                                {s.done ? '✓' : i + 1}
                            </div>
                            <div style={{ fontSize: 11, color: step === s.key ? C.green : C.textSub, fontWeight: step === s.key ? 600 : 400 }}>{s.label}</div>
                        </div>
                        {i < steps.length - 1 && <div style={{ height: 2, flex: 1, background: steps[i + 1].done || s.done ? C.greenMid : C.border, marginBottom: 20 }} />}
                    </div>
                ))}
            </div>

            {step === 'license' && (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>🪪 Driver's License</div>
                        {docStatusBadge(docStatus?.driversLicenseStatus || 'Pending')}
                    </div>
                    {docStatus?.driversLicenseRejectionReason && (
                        <div style={{ background: '#FDECEA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#9B1C1C' }}>
                            ✗ Rejected: {docStatus.driversLicenseRejectionReason}
                        </div>
                    )}
                    <Input label="License number" value={licNumber} onChange={setLicNumber} placeholder="DL12345678" />
                    <Input label="Expiration date" value={licExpiry} onChange={setLicExpiry} type="date" />
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Photo of license (front)</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: `2px dashed ${C.border}`, borderRadius: 8, cursor: 'pointer', background: licPhoto ? C.greenBg : C.bg }}>
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && handlePhoto(e.target.files[0], setLicPhoto, setLicPhotoName)} />
                            <span style={{ fontSize: 20 }}>📷</span>
                            <span style={{ fontSize: 13, color: licPhoto ? C.green : C.textSub }}>{licPhotoName || 'Click to upload photo'}</span>
                            {licPhoto && <span style={{ color: C.green, fontWeight: 600, fontSize: 12, marginLeft: 'auto' }}>✓</span>}
                        </label>
                    </div>
                    {licPhoto && <img src={licPhoto} alt="License preview" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />}
                    <Btn onClick={submitLicense} disabled={loading} fullWidth>{loading ? 'Submitting...' : 'Submit license →'}</Btn>
                    {docStatus?.driversLicenseStatus === 'Submitted' && (
                        <div style={{ textAlign: 'center', marginTop: 12 }}>
                            <Btn variant="outline" size="sm" onClick={() => setStep('insurance')}>Already submitted — continue →</Btn>
                        </div>
                    )}
                </Card>
            )}

            {step === 'insurance' && (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>📄 Proof of Insurance</div>
                        {docStatusBadge(docStatus?.insuranceStatus || 'Pending')}
                    </div>
                    {docStatus?.insuranceRejectionReason && (
                        <div style={{ background: '#FDECEA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#9B1C1C' }}>
                            ✗ Rejected: {docStatus.insuranceRejectionReason}
                        </div>
                    )}
                    <Input label="Insurance provider" value={insProvider} onChange={setInsProvider} placeholder="State Farm, Geico, etc." />
                    <Input label="Policy number" value={insPolicy} onChange={setInsPolicy} placeholder="POL-123456" />
                    <Input label="Expiration date" value={insExpiry} onChange={setInsExpiry} type="date" />
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Photo of insurance card</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: `2px dashed ${C.border}`, borderRadius: 8, cursor: 'pointer', background: insPhoto ? C.greenBg : C.bg }}>
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && handlePhoto(e.target.files[0], setInsPhoto, setInsPhotoName)} />
                            <span style={{ fontSize: 20 }}>📷</span>
                            <span style={{ fontSize: 13, color: insPhoto ? C.green : C.textSub }}>{insPhotoName || 'Click to upload photo'}</span>
                            {insPhoto && <span style={{ color: C.green, fontWeight: 600, fontSize: 12, marginLeft: 'auto' }}>✓</span>}
                        </label>
                    </div>
                    {insPhoto && <img src={insPhoto} alt="Insurance preview" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => setStep('license')} size="sm">← Back</Btn>
                        <Btn onClick={submitInsurance} disabled={loading} fullWidth>{loading ? 'Submitting...' : 'Submit insurance →'}</Btn>
                    </div>
                    {docStatus?.insuranceStatus === 'Submitted' && (
                        <div style={{ textAlign: 'center', marginTop: 12 }}>
                            <Btn variant="outline" size="sm" onClick={() => setStep('vehicle')}>Already submitted — continue →</Btn>
                        </div>
                    )}
                </Card>
            )}

            {step === 'vehicle' && (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>🚗 Vehicle Information</div>
                        {docStatusBadge(docStatus?.vehicleStatus || 'Pending')}
                    </div>
                    {docStatus?.vehicleRejectionReason && (
                        <div style={{ background: '#FDECEA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#9B1C1C' }}>
                            ✗ Rejected: {docStatus.vehicleRejectionReason}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}><Input label="Make" value={vehMake} onChange={setVehMake} placeholder="Ford, Chevy..." /></div>
                        <div style={{ flex: 1 }}><Input label="Model" value={vehModel} onChange={setVehModel} placeholder="F-150, Silverado..." /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}><Input label="Year" value={vehYear} onChange={setVehYear} type="number" placeholder="2020" /></div>
                        <div style={{ flex: 1 }}><Input label="Color" value={vehColor} onChange={setVehColor} placeholder="White, Black..." /></div>
                    </div>
                    <Input label="License plate" value={vehPlate} onChange={setVehPlate} placeholder="ABC-1234" />
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Vehicle photo (optional)</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: `2px dashed ${C.border}`, borderRadius: 8, cursor: 'pointer', background: vehPhoto ? C.greenBg : C.bg }}>
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && handlePhoto(e.target.files[0], setVehPhoto, setVehPhotoName)} />
                            <span style={{ fontSize: 20 }}>📷</span>
                            <span style={{ fontSize: 13, color: vehPhoto ? C.green : C.textSub }}>{vehPhotoName || 'Click to upload photo (optional)'}</span>
                            {vehPhoto && <span style={{ color: C.green, fontWeight: 600, fontSize: 12, marginLeft: 'auto' }}>✓</span>}
                        </label>
                    </div>
                    {vehPhoto && <img src={vehPhoto} alt="Vehicle preview" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => setStep('insurance')} size="sm">← Back</Btn>
                        <Btn onClick={submitVehicle} disabled={loading} fullWidth>{loading ? 'Submitting...' : 'Submit vehicle →'}</Btn>
                    </div>
                </Card>
            )}

            {step === 'done' && (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>
                        {docStatus?.onboardingStatus === 'Approved' ? '✅' : '⏳'}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: docStatus?.onboardingStatus === 'Approved' ? C.green : C.text }}>
                        {docStatus?.onboardingStatus === 'Approved' ? 'All documents approved!' : 'Documents submitted — under review'}
                    </div>
                    <div style={{ fontSize: 14, color: C.textSub, lineHeight: 1.6 }}>
                        {docStatus?.onboardingStatus === 'Approved'
                            ? "You're fully verified and can start accepting jobs."
                            : "An admin will review your documents shortly. You'll be notified when approved."}
                    </div>
                    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                        {[
                            { label: "🪪 Driver's License", status: docStatus?.driversLicenseStatus, step: 'license' },
                            { label: '📄 Insurance', status: docStatus?.insuranceStatus, step: 'insurance' },
                            { label: '🚗 Vehicle', status: docStatus?.vehicleStatus, step: 'vehicle' },
                        ].map(item => (
                            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.bg, borderRadius: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {(item.status === 'Rejected' || item.status === 'Expired') && (
                                        <Btn size="sm" variant="outline" onClick={() => setStep(item.step as any)}>
                                            {item.status === 'Expired' ? '↩ Renew' : 'Resubmit'}
                                        </Btn>
                                    )}
                                    {item.status === 'Approved' && <span style={{ color: C.green, fontWeight: 600, fontSize: 12 }}>✓ Approved</span>}
                                    {item.status === 'Submitted' && <span style={{ color: '#3730A3', fontWeight: 600, fontSize: 12 }}>⏳ Under review</span>}
                                    {item.status === 'Rejected' && <span style={{ color: C.coral, fontWeight: 600, fontSize: 12 }}>✗ Rejected</span>}
                                    {item.status === 'Expired' && <span style={{ color: '#854F0B', fontWeight: 600, fontSize: 12 }}>⚠ Expired</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}

// ── Web Loading Job Card ──────────────────────────────────────────────────────

function WebLoadingJobCard({ job, onRefresh }: { job: any; onRefresh: () => void }) {
    const [loading, setLoading] = useState(false);
    const [pinging, setPinging] = useState(false);
    const [lastPing, setLastPing] = useState<string | null>(null);
    const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const sendLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                pos => {
                    fetch(`${API_BASE}/api/dumpster/loading-jobs/${job.id}/location`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('br_token')}` },
                        body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude })
                    }).catch(() => {});
                },
                () => {}
            );
        }
    };

    // Auto-ping when EnRoute or InProgress
    useEffect(() => {
        if (job.status === 'EnRoute' || job.status === 'InProgress') {
            setPinging(true);
            sendLocation();
            setLastPing(new Date().toLocaleTimeString());
            pingIntervalRef.current = setInterval(() => {
                sendLocation();
                setLastPing(new Date().toLocaleTimeString());
            }, 5000);
        } else {
            setPinging(false);
            if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
        }
        return () => { if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; } };
    }, [job.status, job.id]);

    const doAction = async (action: () => Promise<any>, msg: string) => {
        setLoading(true);
        try { await action(); onRefresh(); if (msg) alert(msg); }
        catch (e: any) { alert(e.message); }
        finally { setLoading(false); }
    };

    const statusColor = () => {
        switch (job.status) {
            case 'Accepted': return '#6366F1';
            case 'InProgress': return C.greenMid;
            case 'PendingApproval': return '#854F0B';
            default: return C.textMuted;
        }
    };

    return (
        <Card style={{ borderLeft: `4px solid ${statusColor()}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>🚛 Loading Job</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: statusColor(), background: `${statusColor()}18`, padding: '2px 10px', borderRadius: 20 }}>
                            {job.status}
                        </span>
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>📍 {job.deliveryAddress}</div>
                    {job.orderStatus === 'Delivered' && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', background: '#16a34a22', borderRadius: 8, padding: '3px 10px', marginBottom: 6, display: 'inline-block' }}>
                            🚛 Dumpster on site — ready to load
                        </div>
                    )}
                    {job.loadingScheduledAt && (
                        <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>
                            📅 {new Date(job.loadingScheduledAt).toLocaleDateString()} {new Date(job.loadingScheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    )}
                    <div style={{ fontSize: 12, color: C.textMuted }}>$8/hr</div>
                    {job.clockedInAt && (
                        <div style={{ fontSize: 12, color: C.greenMid, marginTop: 4 }}>
                            ⏱ Clocked in: {new Date(job.clockedInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    )}
                    {pinging && (
                        <div style={{ fontSize: 12, color: C.greenMid, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.greenMid }} />
                            Sharing location · Last: {lastPing}
                        </div>
                    )}
                    {job.hoursWorked && (
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.greenMid, marginTop: 4 }}>
                            {job.hoursWorked.toFixed(2)}h worked · ${(job.finalCost * 0.8).toFixed(2)}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {job.status === 'Accepted' && (
                    <Btn size="sm" onClick={() => doAction(
                        () => api.updateLoadingJobStatus(job.id, 'EnRoute'), ''
                    )} disabled={loading}>🚗 En route</Btn>
                )}
                {job.status === 'EnRoute' && (
                    <Btn size="sm" onClick={() => doAction(
                        () => api.updateLoadingJobStatus(job.id, 'OnSite'), ''
                    )} disabled={loading}>📍 Arrived on site</Btn>
                )}
                {job.status === 'OnSite' && (
                    <Btn size="sm" onClick={() => doAction(
                        () => api.clockInLoadingJob(job.id), ''
                    )} disabled={loading}>⏱ Clock in — start loading</Btn>
                )}
                {job.status === 'InProgress' && (
                    <Btn size="sm" variant="outline" onClick={() => {
                        if (window.confirm('Clock out? This will submit your hours for customer approval.')) {
                            doAction(() => api.clockOutLoadingJob(job.id),
                                'Clocked out! Waiting for customer to approve hours.');
                        }
                    }} disabled={loading}>⏹ Clock out</Btn>
                )}
                {job.status === 'PendingApproval' && (
                    <div style={{ background: '#FFF7E6', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#854F0B', fontWeight: 600 }}>
                        ⏳ Awaiting customer approval · {job.hoursWorked?.toFixed(2)}h · ${job.finalCost?.toFixed(2)}
                    </div>
                )}
                {job.status === 'Completed' && (
                    <div style={{ background: C.greenBg, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.green, fontWeight: 600 }}>
                        ✅ Completed · ${job.finalCost?.toFixed(2)} earned
                    </div>
                )}
            </div>
        </Card>
    );
}

// ── TREK-68: Availability View ────────────────────────────────────────────────

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIME_OPTIONS = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
];

interface SlotDraft { dayOfWeek: string; startTime: string; endTime: string; }

// ── Skills & certifications (TREK-75) — read-only, admin manages the actual list ──

function SkillsView() {
    const [skills, setSkills] = useState<ContractorSkillDto[] | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.getMyContractorSkills().then(setSkills).catch(e => setError(e.message));
    }, []);

    return (
        <div style={{ maxWidth: 480 }}>
            <Card>
                <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>🔧 My Skills & Certifications</h3>
                <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 16px' }}>
                    Managed by your admin — these determine which job types you're eligible to be dispatched for.
                </p>
                <Alert message={error} />
                {skills === null ? <Spinner /> : skills.length === 0 ? (
                    <div style={{ color: C.textMuted, fontSize: 13, fontStyle: 'italic' }}>No skills on file yet.</div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {skills.map(s => (
                            <span key={s.id} style={{ fontSize: 13, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 20, padding: '4px 12px' }}>
                                {s.name}
                            </span>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

function AvailabilityView() {
    const [data, setData] = useState<ContractorAvailabilityResponse | null>(null);
    const [slots, setSlots] = useState<SlotDraft[]>([]);
    const [blockedDate, setBlockedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        api.getMyAvailability().then(d => {
            setData(d);
            setSlots(d.weeklySlots.map(s => ({ dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime })));
        }).catch(e => setError(e.message))
        .finally(() => setLoading(false));
    }, []);

    const addSlot = (day: string) => {
        setSlots(prev => [...prev, { dayOfWeek: day, startTime: '09:00', endTime: '17:00' }]);
    };

    const removeSlot = (idx: number) => {
        setSlots(prev => prev.filter((_, i) => i !== idx));
    };

    const updateSlot = (idx: number, field: keyof SlotDraft, value: string) => {
        setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    };

    const saveSlots = async () => {
        setSaving(true);
        setError(''); setSuccess('');
        try {
            await api.setMyAvailabilitySlots(slots);
            setSuccess('Availability saved!');
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const addBlockedDate = async () => {
        if (!blockedDate) return;
        setSaving(true);
        setError(''); setSuccess('');
        try {
            await api.addBlockedDate(blockedDate, '');
            const d = await api.getMyAvailability();
            setData(d);
            setBlockedDate('');
            setSuccess('Date blocked.');
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const removeBlockedDate = async (date: string) => {
        try {
            await api.removeBlockedDate(date);
            const d = await api.getMyAvailability();
            setData(d);
        } catch (e: any) { setError(e.message); }
    };

    if (loading) return <Spinner />;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {error && <Alert message={error} />}
            {success && <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.green, fontWeight: 600 }}>{success}</div>}

            {/* Weekly availability */}
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Weekly Availability</h3>
                    <Btn size="sm" onClick={saveSlots} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
                </div>

                {DAYS.map(day => {
                    const daySlots = slots.map((s, i) => ({ ...s, idx: i })).filter(s => s.dayOfWeek === day);
                    return (
                        <div key={day} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{day}</span>
                                <button onClick={() => addSlot(day)} style={{ background: 'none', border: `1px solid ${C.greenMid}`, color: C.greenMid, borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ Add slot</button>
                            </div>
                            {daySlots.length === 0 && <div style={{ fontSize: 12, color: C.textMuted }}>Unavailable</div>}
                            {daySlots.map(s => (
                                <div key={s.idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                    <select value={s.startTime} onChange={e => updateSlot(s.idx, 'startTime', e.target.value)}
                                        style={{ padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13 }}>
                                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <span style={{ color: C.textSub, fontSize: 13 }}>to</span>
                                    <select value={s.endTime} onChange={e => updateSlot(s.idx, 'endTime', e.target.value)}
                                        style={{ padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13 }}>
                                        {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <button onClick={() => removeSlot(s.idx)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </Card>

            {/* Blocked dates */}
            <Card>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Blocked Dates</h3>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <input type="date" value={blockedDate} onChange={e => setBlockedDate(e.target.value)}
                        style={{ padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, flex: 1 }} />
                    <Btn size="sm" onClick={addBlockedDate} disabled={saving || !blockedDate}>Block date</Btn>
                </div>
                {(data?.blockedDates ?? []).length === 0 && <div style={{ fontSize: 13, color: C.textMuted }}>No blocked dates.</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(data?.blockedDates ?? []).map(d => (
                        <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '4px 10px' }}>
                            <span style={{ fontSize: 13, color: '#B91C1C', fontWeight: 500 }}>{d}</span>
                            <button onClick={() => removeBlockedDate(d)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}

// ── Contractor Calendar View ──────────────────────────────────────────────────

function ContractorCalendarView({ myJobs, onRefresh }: { myJobs: ContractorJob[]; onRefresh: () => void }) {
    const today = new Date();
    const toYMD = (d: Date) => d.toISOString().slice(0, 10);
    const todayYMD = toYMD(today);

    const [displayYear, setDisplayYear] = useState(today.getFullYear());
    const [displayMonth, setDisplayMonth] = useState(today.getMonth());
    const [selectedDate, setSelectedDate] = useState(todayYMD);
    const [refreshing, setRefreshing] = useState(false);

    const activeJobs = myJobs.filter(j => !['Completed', 'Cancelled'].includes(j.status));

    // Group jobs by date key
    const jobsByDate = React.useMemo(() => {
        const map: Record<string, ContractorJob[]> = {};
        activeJobs.forEach(j => {
            const key = j.scheduledDate
                ? j.scheduledDate.slice(0, 10)
                : j.requestedDate
                    ? j.requestedDate.slice(0, 10)
                    : 'unscheduled';
            if (!map[key]) map[key] = [];
            map[key].push(j);
        });
        return map;
    }, [activeJobs]);

    const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
    const firstDay = new Date(displayYear, displayMonth, 1).getDay();
    const cells: (number | null)[] = [
        ...Array(firstDay).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
    while (cells.length % 7 !== 0) cells.push(null);

    const monthName = new Date(displayYear, displayMonth, 1)
        .toLocaleString('en-US', { month: 'long', year: 'numeric' });

    const jobsForSelected = jobsByDate[selectedDate] ?? [];
    const unscheduled = jobsByDate['unscheduled'] ?? [];

    const handleRefresh = async () => {
        setRefreshing(true);
        await onRefresh();
        setRefreshing(false);
    };

    const dotColor = (jobs: ContractorJob[]) => {
        if (jobs.some(j => j.scheduledDate)) return C.green;
        return '#D97706';
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>My Schedule</h2>
                <button onClick={handleRefresh} disabled={refreshing}
                    style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: C.textSub, fontWeight: 600 }}>
                    {refreshing ? 'Refreshing…' : '↻ Refresh'}
                </button>
            </div>

            {/* Month calendar */}
            <Card style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
                {/* Month nav */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
                    <button onClick={() => { if (displayMonth === 0) { setDisplayMonth(11); setDisplayYear(y => y - 1); } else setDisplayMonth(m => m - 1); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: C.green, padding: '0 8px', lineHeight: 1 }}>‹</button>
                    <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 15, color: C.text }}>{monthName}</span>
                    <button onClick={() => { if (displayMonth === 11) { setDisplayMonth(0); setDisplayYear(y => y + 1); } else setDisplayMonth(m => m + 1); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: C.green, padding: '0 8px', lineHeight: 1 }}>›</button>
                </div>

                {/* Day headers */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '6px 8px 2px' }}>
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                        <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: C.textMuted, padding: '4px 0' }}>{d}</div>
                    ))}
                </div>

                {/* Grid rows */}
                <div style={{ padding: '0 8px 10px' }}>
                    {Array.from({ length: cells.length / 7 }, (_, row) => (
                        <div key={row} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                            {cells.slice(row * 7, row * 7 + 7).map((day, col) => {
                                if (!day) return <div key={col} style={{ height: 48 }} />;
                                const ymd = `${displayYear}-${String(displayMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const dayJobs = jobsByDate[ymd] ?? [];
                                const isToday = ymd === todayYMD;
                                const isSelected = ymd === selectedDate;
                                return (
                                    <div key={col} style={{ height: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                        onClick={() => setSelectedDate(ymd)}>
                                        <div style={{
                                            width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: isSelected ? C.green : isToday ? C.greenBg : 'transparent',
                                            border: isToday && !isSelected ? `1px solid ${C.green}` : 'none',
                                            fontWeight: isToday || isSelected ? 700 : 400,
                                            fontSize: 14,
                                            color: isSelected ? '#fff' : isToday ? C.green : C.text,
                                        }}>{day}</div>
                                        {dayJobs.length > 0 && (
                                            <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                                                {dayJobs.slice(0, 3).map((j, i) => (
                                                    <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor([j]) }} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </Card>

            {/* Jobs for selected date */}
            <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {selectedDate === todayYMD ? "Today's Jobs" : new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>

            {jobsForSelected.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 32 }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                    <div style={{ fontSize: 14, color: C.textMuted }}>No jobs scheduled for this day</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                    {jobsForSelected.map(job => (
                        <CalendarJobCard key={job.id} job={job} />
                    ))}
                </div>
            )}

            {/* Unscheduled / awaiting dispatch */}
            {unscheduled.length > 0 && (
                <>
                    <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 700, color: '#EA580C', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        ⚠ Accepted — Awaiting Schedule
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {unscheduled.map(job => (
                            <CalendarJobCard key={job.id} job={job} />
                        ))}
                    </div>
                </>
            )}

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 20, fontSize: 12, color: C.textSub }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.green }} />
                    Confirmed pickup
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#D97706' }} />
                    Customer requested date
                </div>
            </div>
        </div>
    );
}

function CalendarJobCard({ job }: { job: ContractorJob }) {
    const hasScheduled = !!job.scheduledDate;
    const borderColor = hasScheduled ? C.green : '#D97706';
    const dateLabel = hasScheduled
        ? `📅 ${new Date(job.scheduledDate!).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${job.scheduledTimeSlot ? ` · ${job.scheduledTimeSlot}` : ''}`
        : job.requestedDate
            ? `📋 Requested: ${new Date(job.requestedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
            : '⚠ No date set';
    const dateLabelColor = hasScheduled ? C.green : job.requestedDate ? '#D97706' : '#EA580C';

    return (
        <Card style={{ borderLeft: `4px solid ${borderColor}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                        <Badge label={job.status} />
                        <Badge label={job.serviceType} />
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{job.pickupAddress}</div>
                    <div style={{ fontSize: 12, color: dateLabelColor, fontWeight: 600, marginBottom: 4 }}>{dateLabel}</div>
                    <div style={{ fontSize: 13, color: C.textSub }}>{job.estimatedUnits} est. units</div>
                </div>
                <div style={{ textAlign: 'right', marginLeft: 12 }}>
                    <div style={{ fontWeight: 700, color: C.greenMid, fontSize: 16 }}>${payoutFor(job).toFixed(2)}</div>
                </div>
            </div>
        </Card>
    );
}
